import SafeImg from "../components/SafeImg.jsx";
import { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import { findCoupon } from "../utils/coupons.js";
import { useParams, useNavigate } from "react-router-dom";
import { supa, SUPA_URL, SH, supabaseClient, BACKEND_URL } from "../config/supabase.js";

// Design Studio live renderer — lazy so only stores with a published custom
// design pay for the builder bundle.
const PublishedStore = lazy(() => import("./DesignStudio/PublishedStore.jsx"));
import { api } from "../config/api.js";
import { DEMO_ID, DEMO_USER, DEMO_PRODUCTS } from "../utils/demo.js";
import { Icon, BloomMark, Monogram, Wordmark } from "../components/Icon.jsx";
import { SoldOutLabel } from "../components/SoldOutLabel.jsx";
import { UpiCheckoutModal } from "../components/UpiCheckoutModal.jsx";
import { RestockRequestModal } from "../components/RestockRequestModal.jsx";
import { startRazorpayPayment, loadRazorpaySDK, isHostedPay, startHostedRazorpayCheckout } from "../utils/razorpay.js";
import { buildQrUrl, buildUpiUrl } from "../utils/upi.js";
import { AddonPicker } from "../components/AddonPicker.jsx";
import { activeAddons, resolveAddons, addonsTotal } from "../utils/addons.js";
import { today, effectiveShipping, normCountry, normState, normPincode, applyHolds, buildHeldMap, bogoLabel } from "../utils/helpers.js";
import { quote, feeFlags } from "../utils/fees.js";
import { useMopConfig, methodOpts } from "../hooks/useMopConfig.js";
import { sendOrderEmail } from "../utils/email.js";

import { thumbUrl, uploadImageToSupabase, videoUrl, posterOf } from "../utils/imageUpload.js";
import { AutoVideo } from "../components/AutoVideo.jsx";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { ThemeWrapper } from "../components/ThemeWrapper.jsx";
import { ProductTile } from "../components/ProductTile.jsx";
import { InstagramChip } from "../components/InstagramChip.jsx";
import { StoreOffers } from "../components/PromoTiles.jsx";
import { getTheme } from "../config/theme.js";
import { ModalImageSwiper } from "../components/ModalImageSwiper.jsx";
import { advancedHasContent } from "../components/AdvancedVariations.jsx";
import { ProductReviewsQNA } from "../components/ProductReviewsQNA.jsx";
import { ProductRecommendations } from "../components/ProductRecommendations.jsx";
import { RatingBadge } from "../components/RatingBadge.jsx";
import { useAvgRatings } from "../hooks/useAvgRatings.js";
import { setVisibleInterval } from "../utils/visibleInterval.js";
import { collectionFromSearch, withCollectionParam } from "../utils/collectionLink.js";

// A product's collections (multi), with back-compat to the legacy single collection_id.
const pColIds = (p) => Array.isArray(p?.collection_ids) ? p.collection_ids.filter(Boolean)
  : (p?.collection_id ? [p.collection_id] : []);
const pInCol = (p, cid) => pColIds(p).includes(cid);

// ─── VIDEO URL HELPER ─────────────────────────────────────────────────────────
// Tolerate query strings (signed URLs) + HLS .m3u8 — old regex broke on ?query.
const isVideoUrl = url => /\.(mp4|mov|webm|m3u8)(\?|$)/i.test(url || "");

// ─── IMAGE/VIDEO CAROUSEL WRAPPER ─────────────────────────────────────────────
// Used on product cards to support swipe + dot indicators + hover arrows
// `badge` = corner ribbon drawn over the image (used for "Buy 2 Get 1 Free").
// Sits top-RIGHT so it never collides with the top-left SALE/discount badge.
function CardCarousel({ images, onClick, style, children, soldOut, badge }) {
  const count = images.length;
  const [imgIdx, setImgIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [dragPx, setDragPx] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const elRef = useRef(null);
  const startX = useRef(null);
  const startY = useRef(null);

  // Every image is a real <img> (via the children render-prop, so each caller's own thumbUrl
  // size is preserved) rendered at once in a flex track; the slide
  // position is a single transform driven purely by React state (imgIdx + live drag offset),
  // animated with a CSS transition. loading="lazy" past index 0 lets the browser's own
  // lazy-loading fetch the others ahead of a swipe, no hand-rolled preload needed.
  //
  // Replaces an earlier version that kept ONE <img> and manually choreographed a swipe through
  // a 5-step chain of setTimeout/requestAnimationFrame calls, imperatively mutating a ref's DOM
  // style at each step — and a SEPARATE hand-rolled IntersectionObserver preload before that.
  // That chain could be abandoned mid-sequence (a re-render, a second swipe arriving before the
  // first settled) and leave the track's transform, or the animLock guard that blocked all
  // further swipes, stuck in a partial state: exactly the "half the image is so far away with a
  // gap in the middle" / "still does not slide" reports. A transform that's always a pure
  // function of current state can't get stuck; there's no multi-step sequence to interrupt, and
  // no preload burst to compete with the images actually on screen.
  const goTo = (i) => { setSnapping(true); setImgIdx(((i % count) + count) % count); setDragPx(0); setTimeout(() => setSnapping(false), 260); };
  const prevImg = (e) => { e.stopPropagation(); goTo(imgIdx - 1); };
  const nextImg = (e) => { e.stopPropagation(); goTo(imgIdx + 1); };

  // Non-passive touchmove is required to preventDefault() the page's own vertical scroll while
  // swiping horizontally — React's JSX touch handlers are passive by default and can't do this.
  useEffect(() => {
    const el = elRef.current;
    if (!el || count <= 1) return;
    const onStart = (e) => { startX.current = e.touches[0].clientX; startY.current = e.touches[0].clientY; setSnapping(false); };
    const onMove = (e) => {
      if (startX.current === null) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = Math.abs(e.touches[0].clientY - startY.current);
      if (Math.abs(dx) > dy && Math.abs(dx) > 6) {
        e.preventDefault();
        setDragPx(imgIdx === 0 && dx > 0 ? dx * 0.3 : imgIdx === count - 1 && dx < 0 ? dx * 0.3 : dx);
      }
    };
    const onEnd = (e) => {
      if (startX.current === null) return;
      const delta = e.changedTouches[0].clientX - startX.current;
      startX.current = null; startY.current = null;
      if (Math.abs(delta) > 40) { goTo(delta < 0 ? imgIdx + 1 : imgIdx - 1); }
      else { setSnapping(true); setDragPx(0); setTimeout(() => setSnapping(false), 260); }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [count, imgIdx]);

  return (
    <div ref={elRef} style={{...style, position:"relative", overflow:"hidden"}}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {/* Animated slide track — every image present at once; transform (imgIdx + live drag
          offset) picks which one shows. Pure function of state, so it always converges to a
          definite position, never a stuck partial one. */}
      {count > 0 && (
        <div style={{
          display:"flex", width:`${count * 100}%`, height:"100%", willChange:"transform",
          transform:`translateX(calc((${-imgIdx * 100}% + ${dragPx}px) / ${count}))`,
          transition: snapping ? "transform 0.25s cubic-bezier(0.25,0.46,0.45,0.94)" : "none",
        }}>
          {images.map((s, i) => (
            <div key={i} style={{ width:`${100 / count}%`, flexShrink:0, height:"100%" }}>
              {/* Only the current photo and its neighbours are mounted. Cards with 8-13 photos used to request ALL of
                  them at once (each a cold CDN resize) which choked phones / in-app browsers → blank images. */}
              {Math.abs(i - imgIdx) > 1 && !(imgIdx === 0 && i === count - 1) && !(imgIdx === count - 1 && i === 0)
                ? <div style={{width:"100%",height:"100%",background:"var(--surface)"}}/>
                : isVideoUrl(s)
                ? (i === imgIdx ? <AutoVideo src={videoUrl(s)} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/> : <div style={{width:"100%",height:"100%",background:"#0D0D0D"}}/>)
                : children ? children(s, i) : <img src={s} alt="" loading={i === 0 ? "eager" : "lazy"} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>}
            </div>
          ))}
        </div>
      )}
      {/* Offer ribbon (e.g. "BUY 2 GET 1 FREE") — must be unmissable on every
          eligible product card, so it sits over the image, not under the price. */}
      {badge && !soldOut && (
        <div style={{position:"absolute",top:8,right:8,zIndex:4,background:"var(--warn-bg)",color:"var(--warn-fg)",border:"1px solid var(--warn-bd)",fontSize:9,fontWeight:800,letterSpacing:"0.06em",padding:"4px 8px",borderRadius:4,textTransform:"uppercase",pointerEvents:"none",lineHeight:1.15,maxWidth:"78%",textAlign:"center"}}>
          {badge}
        </div>
      )}
      {/* Dot indicators */}
      {images.length > 1 && !soldOut && (
        <div style={{position:"absolute",bottom:6,left:0,right:0,display:"flex",justifyContent:"center",gap:4,zIndex:2,pointerEvents:"none"}}>
          {images.slice(0,5).map((_,i) => (
            <div key={i} style={{width:i===imgIdx?14:5,height:5,borderRadius:3,background:i===imgIdx?"var(--teal)":"rgba(20,23,31,0.35)",transition:"all 0.2s"}}/>
          ))}
        </div>
      )}
      {/* Desktop hover arrows */}
      {images.length > 1 && hovered && (
        <>
          <button type="button" aria-label="Previous photo" onClick={prevImg} style={{position:"absolute",left:6,top:"50%",transform:"translateY(-50%)",background:"var(--white)",border:"1px solid var(--border)",borderRadius:4,width:28,height:28,color:"var(--ink)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",zIndex:5,padding:0}}><Icon n="chevron-left" size={16}/></button>
          <button type="button" aria-label="Next photo" onClick={nextImg} style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"var(--white)",border:"1px solid var(--border)",borderRadius:4,width:28,height:28,color:"var(--ink)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",zIndex:5,padding:0}}><Icon n="chevron-right" size={16}/></button>
        </>
      )}
    </div>
  );
}

// ─── DARK CARD WITH CAROUSEL ──────────────────────────────────────────────────
function DarkCardWithCarousel({ p, i, isMobile, soldOut, hasDiscount, bogoBadge, hasVars, cardItem, cardQty, cardMore, cardImages, onOpen, onRemove, onAdd, onCart, onBuy, onRestockRequest, paymentsPaused = false, rating = null }) {
  return (
    <div className="fadeUp dark-prod-card"
      style={{cursor:"pointer",background:"var(--white)",border:"1px solid var(--border)",borderRadius:4,overflow:"hidden",position:"relative",
        animation:`fadeUp 0.45s ease ${i*0.06}s both`}}
      onClick={onOpen}>
      {hasDiscount&&!soldOut&&(
        <span style={{position:"absolute",top:10,left:0,zIndex:3,background:"var(--red)",color:"var(--on-accent)",fontSize:12,fontWeight:700,padding:"3px 8px",borderRadius:"0 2px 2px 0",pointerEvents:"none"}}>{Math.round((Number(p.mrp)-Number(p.price))/Number(p.mrp)*100)}% off</span>
      )}
      <CardCarousel images={cardImages} style={{aspectRatio:"1/1",background:"var(--surface)"}} onClick={onOpen} soldOut={soldOut} badge={bogoBadge}>
        {(src) => (
          <SafeImg src={thumbUrl(src,500)} fallback={src} alt={p.name} loading="lazy"
            style={{width:"100%",height:"100%",objectFit:"cover",display:"block",
              transition:"transform 0.45s ease"}}/>
        )}
      </CardCarousel>
      {/* Badges inside image area — we need absolute positioning, render as overlay */}
      <div style={{position:"relative",marginTop:-1}}>
        {/* (badges are inside CardCarousel's slot child, but since we pass children as render-prop, put badges outside as overlay) */}
      </div>
      {/* Text */}
      <div style={{padding:isMobile?"10px 10px 12px":"12px 12px 14px"}}>
        <RatingBadge rating={rating} dark style={{marginBottom:6}}/>
        <div className="sf-pname" style={{fontSize:14,fontWeight:500,color:"var(--ink)",lineHeight:1.3,marginBottom:6}}>{p.name}</div>
        <div style={{display:"flex",alignItems:"baseline",gap:7,marginBottom:8}}>
          <span style={{fontSize:16,fontWeight:700,color:"var(--ink)"}}>₹{(Number(p.price)||0).toLocaleString()}</span>
          {hasDiscount&&<span style={{fontSize:12,color:"var(--ink-soft)",textDecoration:"line-through"}}>₹{Number(p.mrp).toLocaleString()}</span>}
        </div>
        {soldOut&&<SoldOutLabel block onRestock={onRestockRequest||null} />}
        {!soldOut&&<div onClick={e=>e.stopPropagation()}>
          {hasVars
            ? <button onClick={onOpen} style={{width:"100%",height:36,fontSize:13,fontWeight:600,background:"var(--ink)",color:"var(--on-ink)",border:"none",borderRadius:4,cursor:"pointer",fontFamily:"inherit"}}>View options</button>
            : cardItem
              ? <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",border:"1px solid var(--border-mid)",background:"var(--surface)",borderRadius:4,padding:"0 12px",height:36}}>
                  <button onClick={e=>{e.stopPropagation();onRemove();}} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,fontWeight:700,color:"var(--ink)",lineHeight:1}}>−</button>
                  <span style={{fontSize:13,fontWeight:600,color:"var(--ink)",fontFamily:"inherit"}}>{cardQty} in cart</span>
                  <button onClick={e=>{e.stopPropagation();if(cardMore)onAdd();}} disabled={!cardMore} style={{background:"none",border:"none",cursor:cardMore?"pointer":"not-allowed",fontSize:16,fontWeight:700,color:cardMore?"var(--ink)":"var(--ink-soft)",lineHeight:1}}>+</button>
                </div>
              : <div style={{display:"flex",gap:4}}>
                  {!paymentsPaused && <button onClick={e=>{e.stopPropagation();onCart();}} style={{flex:1,height:36,fontSize:13,fontWeight:600,background:"transparent",color:"var(--ink)",border:"1px solid var(--border-mid)",borderRadius:4,cursor:"pointer",fontFamily:"inherit"}}>Add to cart</button>}
                  {paymentsPaused
                    ? <button disabled style={{flex:1,height:36,fontSize:13,fontWeight:600,background:"var(--surface)",color:"var(--ink-soft)",border:"none",cursor:"not-allowed",borderRadius:4}}><Icon n="locked" size="1.1em" inline /> Payments paused</button>
                    : <button onClick={e=>{e.stopPropagation();onBuy();}} style={{flex:1,height:36,fontSize:13,fontWeight:600,background:"var(--ink)",color:"var(--on-ink)",border:"none",borderRadius:4,cursor:"pointer",fontFamily:"inherit"}}>Buy now</button>
                  }
                </div>
          }
        </div>}
      </div>
    </div>
  );
}

// ─── STOREFRONT PAGE — reads /store/:slug from URL ───────────────────────────
// This is what customers land on when they visit a store link directly
// A store whose catalogue lists the same product id twice would render duplicate React keys (and double cards).
const uniqueById = (list) => { const seen = new Set(); return (Array.isArray(list) ? list : []).filter(p => p && !seen.has(p.id) && seen.add(p.id)); };

export function StorefrontPage({ users, allProducts, allOrders, setAllOrders, cart, setCart, customer, setCurrentStore, setUsers, paymentsPaused = false, slugOverride = null }) {
  const { slug: paramSlug } = useParams();
  const slug = slugOverride || paramSlug;  // slugOverride = served from storename.bloomhq.in
  const navigate  = useNavigate();
  const [storeUser,   setStoreUser]   = useState(null);
  const [products,    setProducts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);
  const [loadFailed,  setLoadFailed]  = useState(false);
  const [reloadTick,  setReloadTick]  = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setLoadFailed(false);
      // Check demo store from memory first
      const demoUser = users.find(u => u.store_slug === slug || u.storeSlug === slug);
      if (demoUser?.id === DEMO_ID) {
        setStoreUser(demoUser);
        setProducts(uniqueById(allProducts[demoUser.id] || []));
        if (setCurrentStore) setCurrentStore(demoUser.id);
        setLoading(false);
        return;
      }

      // Fetch store user — with one automatic retry on network failure.
      // supa.select() swallows errors and returns [], which would incorrectly
      // show "store not found" when Supabase is slow or Instagram's in-app
      // browser fires the request before the network is ready.
      const fetchUser = async (s) => {
        try {
          const r = await fetch(`${SUPA_URL}/rest/v1/public_stores?store_slug=eq.${encodeURIComponent(s)}`, { headers: SH });
          if (!r.ok) return null; // server error — treat as transient, trigger retry
          return await r.json();
        } catch { return null; } // network error — trigger retry
      };

      let rows = await fetchUser(slug);
      // Up to 3 retries with growing waits — slow phones / Instagram's in-app browser often need a second or two.
      for (let n = 1; rows === null && n <= 3; n++) {
        await new Promise(res => setTimeout(res, 1000 * n));
        rows = await fetchUser(slug);
      }

      // Slug tolerance: links shared from Instagram/bios often carry stray leading or
      // trailing dashes that don't match a store's clean slug (a relic of older slug
      // generation on emoji/space-heavy store names). On a genuine miss, retry once with
      // those trimmed before declaring the store missing.
      if (Array.isArray(rows) && rows.length === 0) {
        const trimmed = slug.replace(/^-+|-+$/g, "");
        if (trimmed && trimmed !== slug) {
          const alt = await fetchUser(trimmed);
          if (Array.isArray(alt) && alt.length > 0) rows = alt;
        }
      }

      // After retries: null = persistent network error (show a retry screen, NOT "Store not found"), [] = truly not found
      if (rows === null) { setLoadFailed(true); setLoading(false); return; }
      if (rows.length === 0) { setNotFound(true); setLoading(false); return; }
      // Safety net for slug collisions: if more than one store somehow shares this
      // slug, prefer the live (approved, non-banned) one so an empty/unapproved
      // duplicate can never shadow an established store ("Store not found").
      const user = rows.find(u => u.approved !== false && !u.banned) || rows[0];
      if (user.banned || user.approved === false) { setNotFound(true); setLoading(false); return; }

      // Always fetch fresh — cached products have stale stock values after purchases
      const prods = await supa.selectRetry("bloom_products", `select=id,user_id,name,price,mrp,img,images,stock,category,collection_id,collection_ids,free_shipping,bogo_eligible,is_pinned,variations,variation_images,advanced_variations,description,created_at,is_live,custom_question,require_upload&user_id=eq.${user.id}&is_live=eq.true&order=created_at.desc`);

      if (prods === null) { setLoadFailed(true); setLoading(false); return; }   // never show an empty shop because of a network blip
      setStoreUser(user);
      setProducts(uniqueById(prods));
      api.trackView(user.id); // analytics: count this storefront view (fire-and-forget)
      if (setCurrentStore) setCurrentStore(user.id);
      // Update the in-memory user with fresh data (template etc.)
      // MERGE, never replace: `user` here comes from public_stores, which deliberately has NO
      // private fields (upi_id / phone / email). Overwriting the row outright wiped the logged-in
      // seller's own upi_id from app state the moment they viewed a storefront — Settings then
      // rendered an empty UPI box ("my UPI disappeared"). Spreading the public row OVER the
      // existing one refreshes public fields (template etc.) while private fields survive.
      if (setUsers) setUsers(prev => prev.map(u => u.id === user.id ? { ...u, ...user } : u).concat(prev.find(u => u.id === user.id) ? [] : [user]));
      setLoading(false);
    };
    load();
  }, [slug, reloadTick]);

  // ── Realtime stock updates ────────────────────────────────────────────────
  // Subscribe to bloom_products changes for this store.
  // When any product's stock/variations change, update local state instantly.
  // Customers see Sold Out in real time — no refresh needed.
  useEffect(() => {
    if (!storeUser?.id || storeUser.id === DEMO_ID) return;
    const channel = supabaseClient
      .channel(`stock-${storeUser.id}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "bloom_products",
        filter: `user_id=eq.${storeUser.id}`,
      }, (payload) => {
        setProducts(prev => prev.map(p =>
          p.id === payload.new.id ? { ...p, stock: payload.new.stock, variations: payload.new.variations, advanced_variations: payload.new.advanced_variations } : p
        ));
      })
      .subscribe();
    return () => { supabaseClient.removeChannel(channel); };
  }, [storeUser?.id]);

  const handleOrderPlaced = async (storeId, order) => {
    setAllOrders(prev => ({ ...prev, [storeId]: [order, ...(prev[storeId]||[])] }));
  };

  // ── Design Studio: published custom design replaces the default template ──
  // Loaded lazily so shoppers of normal (template) stores never download the
  // builder bundle. `null` = not checked yet, `false` = this store has none.
  const [pubDesign, setPubDesign] = useState(null);
  useEffect(() => {
    if (!storeUser?.id) return;
    let cancelled = false;
    setPubDesign(null);
    fetch(`${BACKEND_URL}/api/pro/public-design/${storeUser.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setPubDesign(d?.published ? d : false); })
      .catch(() => { if (!cancelled) setPubDesign(false); });
    return () => { cancelled = true; };
  }, [storeUser?.id]);

  if (loading) return (
    <div style={{minHeight:"80vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
      <Wordmark size={40}/>
      <p style={{fontSize:18,fontWeight:600,color:"var(--teal)"}}>Loading store…</p>
    </div>
  );

  if (loadFailed) return (
    <div style={{minHeight:"80vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,textAlign:"center",padding:"0 24px"}}>
      <Wordmark size={40}/>
      <h2 style={{fontSize:22}}>Couldn't load this store</h2>
      <p style={{fontSize:14,color:"var(--ink-soft)",maxWidth:320,lineHeight:1.5}}>Your connection looks slow. The store is fine — tap to try again.</p>
      <button className="btn-pink" style={{padding:"12px 28px",borderRadius:4,fontSize:14}} onClick={()=>setReloadTick(t=>t+1)}>Try again</button>
    </div>
  );

  if (notFound) return (
    <div style={{minHeight:"80vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,textAlign:"center",padding:"0 24px"}}>
      <div style={{fontSize:56}}><BloomMark /></div>
      <h2 style={{fontSize:32}}>Store not found</h2>
      <p style={{color:"var(--ink-soft)",fontSize:15}}>The store <strong>{slug}</strong> doesn't exist yet.</p>
      <button className="btn-pink" style={{marginTop:8,borderRadius:4,padding:"12px 28px"}} onClick={()=>navigate("/")}>
        Back to Bloom
      </button>
    </div>
  );

  // Wait for the design check before rendering ANYTHING commerce-facing. Without
  // this, `pubDesign === null` (check still in flight) fell through to the default
  // template, so a custom-design store flashed the plain storefront for a beat
  // before swapping to its real design — on first load AND on every collection jump.
  if (pubDesign === null) return (
    <div style={{minHeight:"80vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
      <Wordmark size={40}/>
      <p style={{fontSize:18,fontWeight:600,color:"var(--teal)"}}>Loading store…</p>
    </div>
  );

  // Published Design Studio design wins over the default template — this is what
  // "everyone sees the new website" means: shoppers AND the owner get this page.
  if (pubDesign && pubDesign.published) {
    return (
      <Suspense fallback={
        <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--ink-soft)"}}>
          Loading store…
        </div>
      }>
        <PublishedStore
          design={pubDesign.design}
          elements={pubDesign.elements}
          storeUser={storeUser}
          cart={cart}
          setCart={setCart}
          customer={customer}
        />
      </Suspense>
    );
  }

  return (
    <Storefront
      storeUser={storeUser}
      products={products}
      setProducts={setProducts}
      onOrderPlaced={handleOrderPlaced}
      cart={cart}
      setCart={setCart}
      setPage={()=>{}}
      customer={customer}
      paymentsPaused={paymentsPaused}
    />
  );
}

// ─── ADDRESS MODAL ────────────────────────────────────────────────────────────
// Shown before Razorpay opens — collects delivery address
// Delivery countries. "India" is the default/domestic case; anything else is an
// international order (flat intl shipping + per-store opt-in gate). "Other" = anywhere else.
// All Indian states + union territories — used for the checkout state picker and the
// seller's per-state shipping rates. Keep this list in sync with the app.
export const INDIAN_STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman & Nicobar Islands","Chandigarh","Dadra & Nagar Haveli and Daman & Diu","Delhi","Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry"];

export const SHIP_COUNTRIES = ["India","United States","United Kingdom","Canada","Australia","United Arab Emirates","Singapore","Germany","France","Netherlands","New Zealand","Ireland","Japan","Malaysia","Saudi Arabia","Qatar","Kuwait","Bahrain","Oman","Nepal","Bangladesh","Sri Lanka","South Africa","Italy","Spain","Sweden","Switzerland","Belgium","Norway","Denmark","Austria","Portugal","Finland","Hong Kong","South Korea","Thailand","Indonesia","Philippines","Mexico","Brazil","Other"];

// Per-country address format rules so a non-India order is guided to the correct format
// instead of accepting anything. dial = country calling code; tel = valid national-number
// digit lengths (excluding the dial code); postal = regex the postal/ZIP must match
// (null + noPostal = country has no postal code); ex = the example shown to the buyer.
export const COUNTRY_RULES = {
  "India":                { dial:"+91",  tel:[10],         postal:/^\d{6}$/,                                        ex:"560001" },
  "United States":        { dial:"+1",   tel:[10],         postal:/^\d{5}(-\d{4})?$/,                               ex:"10001" },
  "United Kingdom":       { dial:"+44",  tel:[10,11],      postal:/^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/,   ex:"SW1A 1AA" },
  "Canada":               { dial:"+1",   tel:[10],         postal:/^[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d$/,            ex:"K1A 0B1" },
  "Australia":            { dial:"+61",  tel:[9],          postal:/^\d{4}$/,                                        ex:"2000" },
  "United Arab Emirates": { dial:"+971", tel:[9],          postal:null, noPostal:true,                              ex:"" },
  "Singapore":            { dial:"+65",  tel:[8],          postal:/^\d{6}$/,                                        ex:"018956" },
  "Germany":              { dial:"+49",  tel:[10,11],      postal:/^\d{5}$/,                                        ex:"10115" },
  "France":               { dial:"+33",  tel:[9],          postal:/^\d{5}$/,                                        ex:"75001" },
  "Netherlands":          { dial:"+31",  tel:[9],          postal:/^\d{4}\s*[A-Za-z]{2}$/,                          ex:"1011 AB" },
  "New Zealand":          { dial:"+64",  tel:[8,9,10],     postal:/^\d{4}$/,                                        ex:"6011" },
  "Ireland":              { dial:"+353", tel:[9],          postal:/^[A-Za-z]\d{2}\s*[A-Za-z0-9]{4}$/,               ex:"D02 AF30" },
  "Japan":                { dial:"+81",  tel:[10,11],      postal:/^\d{3}-?\d{4}$/,                                 ex:"100-0001" },
  "Malaysia":             { dial:"+60",  tel:[9,10],       postal:/^\d{5}$/,                                        ex:"50000" },
  "Saudi Arabia":         { dial:"+966", tel:[9],          postal:/^\d{5}$/,                                        ex:"11564" },
  "Qatar":                { dial:"+974", tel:[8],          postal:null, noPostal:true,                              ex:"" },
  "Kuwait":               { dial:"+965", tel:[8],          postal:/^\d{5}$/,                                        ex:"12345" },
  "Bahrain":              { dial:"+973", tel:[8],          postal:/^\d{3,4}$/,                                      ex:"317" },
  "Oman":                 { dial:"+968", tel:[8],          postal:/^\d{3}$/,                                        ex:"112" },
  "Nepal":                { dial:"+977", tel:[10],         postal:/^\d{5}$/,                                        ex:"44600" },
  "Bangladesh":           { dial:"+880", tel:[10],         postal:/^\d{4}$/,                                        ex:"1000" },
  "Sri Lanka":            { dial:"+94",  tel:[9],          postal:/^\d{5}$/,                                        ex:"00100" },
  "South Africa":         { dial:"+27",  tel:[9],          postal:/^\d{4}$/,                                        ex:"8001" },
  "Italy":                { dial:"+39",  tel:[9,10],       postal:/^\d{5}$/,                                        ex:"00100" },
  "Spain":                { dial:"+34",  tel:[9],          postal:/^\d{5}$/,                                        ex:"28001" },
  "Sweden":               { dial:"+46",  tel:[7,8,9],      postal:/^\d{3}\s*\d{2}$/,                                ex:"111 22" },
  "Switzerland":          { dial:"+41",  tel:[9],          postal:/^\d{4}$/,                                        ex:"8001" },
  "Belgium":              { dial:"+32",  tel:[8,9],        postal:/^\d{4}$/,                                        ex:"1000" },
  "Norway":               { dial:"+47",  tel:[8],          postal:/^\d{4}$/,                                        ex:"0001" },
  "Denmark":              { dial:"+45",  tel:[8],          postal:/^\d{4}$/,                                        ex:"1050" },
  "Austria":              { dial:"+43",  tel:[10,11],      postal:/^\d{4}$/,                                        ex:"1010" },
  "Portugal":             { dial:"+351", tel:[9],          postal:/^\d{4}-?\d{3}$/,                                 ex:"1000-001" },
  "Finland":              { dial:"+358", tel:[9,10],       postal:/^\d{5}$/,                                        ex:"00100" },
  "Hong Kong":            { dial:"+852", tel:[8],          postal:null, noPostal:true,                              ex:"" },
  "South Korea":          { dial:"+82",  tel:[9,10],       postal:/^\d{5}$/,                                        ex:"03187" },
  "Thailand":             { dial:"+66",  tel:[9],          postal:/^\d{5}$/,                                        ex:"10200" },
  "Indonesia":            { dial:"+62",  tel:[9,10,11,12], postal:/^\d{5}$/,                                        ex:"10110" },
  "Philippines":          { dial:"+63",  tel:[10],         postal:/^\d{4}$/,                                        ex:"1000" },
  "Mexico":               { dial:"+52",  tel:[10],         postal:/^\d{5}$/,                                        ex:"01000" },
  "Brazil":               { dial:"+55",  tel:[10,11],      postal:/^\d{5}-?\d{3}$/,                                 ex:"01000-000" },
  "Other":                { dial:"",     tel:null,         postal:null,                                             ex:"Postal / ZIP code" },
};
export const ruleFor = (c) => COUNTRY_RULES[c] || COUNTRY_RULES["Other"];
// Phone valid if its digits (minus an optional leading dial code) match a national
// length for the country. "Other" (tel:null) accepts any 7+ digit number.
export function validPhone(country, raw) {
  const r = ruleFor(country);
  let d = String(raw || "").replace(/\D/g, "");
  const dialDigits = r.dial.replace(/\D/g, "");
  if (dialDigits && d.length > (r.tel ? Math.max(...r.tel) : 7) && d.startsWith(dialDigits)) d = d.slice(dialDigits.length);
  // Drop a national trunk '0' (numbers written like 07… or 090…).
  if (r.tel && d.startsWith("0") && r.tel.includes(d.length - 1)) d = d.slice(1);
  if (!r.tel) return d.length >= 7;
  return r.tel.includes(d.length);
}
// Postal valid per country. noPostal countries → always ok (field is optional there).
export function validPostal(country, raw) {
  const r = ruleFor(country);
  if (r.noPostal) return true;
  const v = String(raw || "").trim();
  if (r.postal) return r.postal.test(v);
  return v.length > 0;
}

export function AddressModal({ customer, onConfirm, onClose, storeUser, shippingResolver, onCountryChange, onStateChange, couponSubtotal = null }) {
  const [form, setForm] = useState({
    name:      customer?.name  || "",
    phone:     customer?.phone || "",
    email:     customer?.email || "",
    instagram: "",
    country:   "India",
    line1:     "",
    line2:     "",
    city:      "",
    state:     "",
    pincode:   "",
  });
  const [errs, setErrs] = useState({});
  const h = e => setForm(f=>({...f,[e.target.name]:e.target.value}));
  // Autofill can leave the country as "" / "IN" / "india" while the dropdown still looks like India — repair it (see normCountry).
  useEffect(() => {
    const n = normCountry(form.country, SHIP_COUNTRIES);
    if (n !== form.country) { setForm(f => ({ ...f, country: n })); onCountryChange && onCountryChange(n); }
  }, [form.country]); // eslint-disable-line react-hooks/exhaustive-deps
  const country = normCountry(form.country, SHIP_COUNTRIES);   // always a real, clean country (India when empty / IN / india)
  const isIndia = country === "India";
  // Same autofill problem for the state and pincode: repair them to the exact list entry / clean 6 digits (India only).
  useEffect(() => {
    if (!isIndia) return;
    const st = normState(form.state, INDIAN_STATES), pc = normPincode("India", form.pincode);
    if (st !== form.state || pc !== form.pincode) {
      setForm(f => ({ ...f, state: st, pincode: pc }));
      if (st !== form.state) onStateChange && onStateChange(st);
    }
  }, [form.state, form.pincode, isIndia]); // eslint-disable-line react-hooks/exhaustive-deps
  const rule = ruleFor(country);   // per-country postal + phone format rules
  // International shipping / eligibility for the chosen country (parent-supplied).
  const intlInfo = (!isIndia && typeof shippingResolver === "function") ? shippingResolver(country) : null;
  const blockedStores = intlInfo?.unshippable || [];
  const canShipThere  = isIndia || blockedStores.length === 0;
  const Err = ({k})=>errs[k]?<p style={{fontSize:11,color:"var(--red)",marginTop:3}}>{errs[k]}</p>:null;
  // Instagram in-app browser → show a tiny, passive hint only (NO button/action — a
  // clickable "open in Safari" caused copy-link confusion, wrong-account logins and
  // cart merges). Just a gentle nudge; the customer opens it themselves if they want.
  const inIAB = /Instagram|FBAN|FBAV|FB_IAB/i.test(navigator.userAgent || "");

  // ── Coupon state (only used when storeUser is passed — i.e. quick-buy flow) ──
  const [couponCode,    setCouponCode]    = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError,   setCouponError]   = useState("");
  const hasCoupons = Array.isArray(storeUser?.coupons) && storeUser.coupons.some(c=>c.active);
  // Buy Now only (storeUser is passed): the store's checkout add-ons the shopper can tick before paying.
  const [addonIds, setAddonIds] = useState([]);
  const quickAddons = storeUser ? activeAddons(storeUser.checkout_addons) : [];
  const toggleQuickAddon = (id) => setAddonIds(cur => cur.includes(String(id)) ? cur.filter(x => x !== String(id)) : [...cur, String(id)]);

  const applyCoupon = () => {
    const allCoupons = Array.isArray(storeUser?.coupons) ? storeUser.coupons : [];
    const { coupon: c, reason } = findCoupon(allCoupons, couponCode, couponSubtotal ?? Infinity);
    if (!c) { setCouponError(reason === "expired" ? "This coupon has expired." : "Invalid or expired coupon code."); return; }
    // Same minimum-order rule checkout enforces — say so here instead of failing at Pay Now.
    if (couponSubtotal != null && Number(c.min_order) > 0 && couponSubtotal < Number(c.min_order)) { setCouponError(`Minimum order ₹${c.min_order} required.`); return; }
    setCouponApplied(c); setCouponError("");
  };
  const removeCoupon = () => { setCouponApplied(null); setCouponCode(""); setCouponError(""); };

  const submit = () => {
    const e = {};
    if (!form.name.trim())  e.name    = "Full name is required";
    // Indian mobile validation: strip +91 / leading 0 / spaces / dashes, then require a
    // real 10-digit number starting 6–9. Keeps junk like "12345" or "000000" out of the DB.
    // Phone: India → strict 10-digit mobile; other countries → any reasonable number
    // (keep the leading + / country code the buyer entered).
    const rule = ruleFor(country);
    let phoneDigits;
    if (isIndia) {
      phoneDigits = form.phone.replace(/\D/g, "");
      if (phoneDigits.length === 12 && phoneDigits.startsWith("91")) phoneDigits = phoneDigits.slice(2);
      if (phoneDigits.length === 11 && phoneDigits.startsWith("0"))  phoneDigits = phoneDigits.slice(1);
      if (!/^[6-9]\d{9}$/.test(phoneDigits)) e.phone = "Enter a valid 10-digit mobile number";
    } else {
      phoneDigits = form.phone.replace(/[^\d+]/g, "");   // keep + and digits
      if (!validPhone(country, form.phone)) e.phone = `Enter a valid ${country} phone number (${rule.dial})`;
    }
    if (!form.email.trim()||!form.email.includes("@")) e.email = "Valid email is required";
    if (!form.line1.trim()) e.line1   = "Address is required";
    if (!form.city.trim())  e.city    = "City is required";
    if (isIndia && !form.state.trim()) e.state = "Please select your state";
    // Postal/ZIP validated against the selected country's format (skipped for the few
    // countries that have no postal code, e.g. UAE / Qatar / Hong Kong).
    if (!rule.noPostal) {
      if (!form.pincode.trim())                          e.pincode = "Postal code is required";
      else if (!validPostal(country, form.pincode)) e.pincode = isIndia ? "Valid 6-digit pincode required" : `Enter a valid ${country} postal code (e.g. ${rule.ex})`;
    }
    // Block checkout to a country a store in the cart doesn't ship to.
    if (!canShipThere) e.country = `${blockedStores.join(", ")} ${blockedStores.length > 1 ? "ship" : "ships"} within India only — remove ${blockedStores.length > 1 ? "them" : "it"} to continue.`;
    if (Object.keys(e).length) { setErrs(e); return; }
    const igHandle = form.instagram.trim().replace(/^@/,"");
    const instaPart = igHandle ? `\nInstagram: @${igHandle}` : "";
    // Store City/State/Pincode/Country as their OWN labeled lines (not crammed into
    // "Address:") so the packing slip can stack them like a proper postal label.
    const addr = `Name: ${form.name}\nPhone: ${phoneDigits}\nEmail: ${form.email}${instaPart}\nAddress: ${[form.line1, form.line2].filter(Boolean).join(", ")}\nCity: ${form.city.trim()}\nState: ${form.state.trim()}\nPincode: ${form.pincode.trim()}\nCountry: ${country}`;
    // 3rd arg = contact (name/phone/email for guest checkouts) + the delivery country
    // and international flag so Cart/Storefront apply intl shipping + send the flag.
    onConfirm(addr, couponApplied || null, { name: form.name.trim(), phone: phoneDigits, email: form.email.trim(), country: country, international: !isIndia, addonIds: quickAddons.length ? addonIds : [] });
  };

  return (
    <div style={{position:"fixed",inset:0,background:"var(--overlay)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div className="card pop-in" style={{width:"100%",maxWidth:440,padding:32,maxHeight:"92vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <h3 style={{fontSize:22}}><Icon n="package" size="1.1em" inline /> Delivery Details</h3>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer"}}><Icon n="x" size={20}/></button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:11}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>FULL NAME *</label>
              <input className={`inp${errs.name?" err":""}`} name="name" value={form.name} onChange={h} placeholder="Full name"/>
              <Err k="name"/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>PHONE *{!isIndia && <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:"var(--ink-soft)"}}> ({rule.dial})</span>}</label>
              <input className={`inp${errs.phone?" err":""}`} name="phone" type="tel" inputMode={isIndia ? "numeric" : "tel"} autoComplete="tel" maxLength={16} value={form.phone} onChange={h} placeholder={isIndia ? "10-digit mobile" : `${rule.dial} number`}/>
              <Err k="phone"/>
            </div>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>EMAIL *</label>
            <input className={`inp${errs.email?" err":""}`} name="email" type="email" value={form.email} onChange={h} placeholder="your@email.com"/>
            <Err k="email"/>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>INSTAGRAM <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional — for emergency contact)</span></label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:14,color:"var(--ink-soft)",fontWeight:700,pointerEvents:"none"}}>@</span>
              <input className="inp" name="instagram" value={form.instagram} onChange={h} placeholder="yourhandle" style={{paddingLeft:32}}/>
            </div>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>COUNTRY *</label>
            <select className="inp" name="country" value={normCountry(form.country, SHIP_COUNTRIES)} autoComplete="off"
              onChange={e => { const n = normCountry(e.target.value, SHIP_COUNTRIES); setForm(f => ({ ...f, country: n })); onCountryChange && onCountryChange(n); }}>
              {SHIP_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Err k="country"/>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>ADDRESS LINE 1 *</label>
            <input className={`inp${errs.line1?" err":""}`} name="line1" value={form.line1} onChange={h} placeholder="House / Flat no., Street, Area"/>
            <Err k="line1"/>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>ADDRESS LINE 2</label>
            <input className="inp" name="line2" value={form.line2} onChange={h} placeholder="Landmark (optional)"/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>CITY *</label>
              <input className={`inp${errs.city?" err":""}`} name="city" value={form.city} onChange={h} placeholder="City"/>
              <Err k="city"/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>STATE{isIndia?" *":""}</label>
              {isIndia
                ? <select className={`inp${errs.state?" err":""}`} name="state" value={normState(form.state, INDIAN_STATES)}
                    onChange={e => { const n = normState(e.target.value, INDIAN_STATES); setForm(f => ({ ...f, state: n })); onStateChange && onStateChange(n); }}>
                    <option value="">Select state</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                : <input className="inp" name="state" value={form.state} onChange={h} placeholder="State / Province"/>
              }
              <Err k="state"/>
            </div>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:"var(--ink-soft)",display:"block",marginBottom:5}}>{rule.noPostal ? "POSTAL CODE" : (isIndia ? "PINCODE *" : "POSTAL / ZIP CODE *")}</label>
            <input className={`inp${errs.pincode?" err":""}`} name="pincode" value={form.pincode} onChange={h} placeholder={rule.noPostal ? "Not required for this country" : (isIndia ? "6-digit pincode" : `e.g. ${rule.ex}`)} maxLength={isIndia ? 6 : 12}/>
            {!isIndia && !rule.noPostal && !errs.pincode && <p style={{fontSize:10,color:"var(--ink-soft)",marginTop:3}}>Format like <strong>{rule.ex}</strong></p>}
            <Err k="pincode"/>
          </div>
          {/* International order — shipping + customs disclaimer, or can't-ship warning */}
          {!isIndia && (
            canShipThere ? (
              <div style={{background:"var(--teal-light)",border:"1px solid var(--teal-mid)",borderRadius:4,padding:"12px 14px"}}>
                <div style={{fontSize:13,fontWeight:700,color:"var(--teal-deep)"}}><Icon n="globe" size="1.1em" inline /> International order · {country}</div>
                {intlInfo && typeof intlInfo.shipping === "number" && (
                  <div style={{fontSize:12,color:"var(--ink)",marginTop:4}}>International shipping: <strong>₹{Number(intlInfo.shipping).toLocaleString()}</strong>{intlInfo.shipping === 0 ? " (free)" : ""}</div>
                )}
                <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:6,lineHeight:1.5}}>Charged in ₹ (INR). Any customs duties or import taxes in the destination country are the buyer's responsibility and are not included.</div>
              </div>
            ) : (
              <div style={{background:"var(--red-light)",border:"1px solid var(--red)",borderRadius:4,padding:"12px 14px"}}>
                <div style={{fontSize:12,fontWeight:700,color:"var(--red)"}}>Can't ship to {country}</div>
                <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:4,lineHeight:1.5}}>{blockedStores.join(", ")} {blockedStores.length > 1 ? "ship" : "ships"} within India only. Remove {blockedStores.length > 1 ? "them" : "it"} from your cart to check out internationally.</div>
              </div>
            )
          )}
          {/* Coupon — only shown for quick-buy (storeUser passed) when store has active coupons */}
          {quickAddons.length>0 && <AddonPicker addons={quickAddons} selectedIds={addonIds} onToggle={toggleQuickAddon} note={`Optional extras from ${storeUser?.store_name || "this store"}`}/>}
          {hasCoupons&&(
            couponApplied ? (
              <div style={{background:"var(--green-bg)",borderRadius:4,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <span style={{fontSize:12,fontWeight:700,color:"var(--green)"}}><Icon n="success" size={12} inline /> {couponApplied.code}</span>
                  <span style={{fontSize:11,color:"var(--green)",marginLeft:8}}>
                    -{couponApplied.discount_type==="percent"?`${couponApplied.discount_value}%`:`₹${couponApplied.discount_value}`} off
                  </span>
                </div>
                <button onClick={removeCoupon} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
              </div>
            ) : (
              <div>
                <div style={{display:"flex",gap:7}}>
                  <input className="inp" placeholder="Coupon code" value={couponCode}
                    onChange={e=>setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={e=>e.key==="Enter"&&applyCoupon()}
                    style={{flex:1,padding:"9px 14px",fontSize:13}}/>
                  <button onClick={applyCoupon}
                    style={{background:"var(--pink-btn)",color:"var(--on-accent)",border:"none",borderRadius:4,padding:"9px 14px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                    Apply
                  </button>
                </div>
                {couponError&&<p style={{fontSize:11,color:"var(--red)",marginTop:5}}>{couponError}</p>}
              </div>
            )
          )}
          {inIAB && (
            <p style={{fontSize:10,lineHeight:1.4,color:"rgba(180,35,24,0.6)",textAlign:"center",marginTop:2,marginBottom:-2}}>
              For a smoother checkout, open this page in your browser via Instagram's ⋯ menu.
            </p>
          )}
          <button className="btn-green" style={{padding:14,borderRadius:4,marginTop:6,fontSize:14}} onClick={submit}>
            Continue to Payment →
          </button>
          <p style={{fontSize:11,color:"var(--ink-soft)",textAlign:"center"}}>UPI · Cards · Wallets · Secured by Razorpay</p>
        </div>
      </div>
    </div>
  );
}

// ─── STOREFRONT ───────────────────────────────────────────────────────────────
export function Storefront({ storeUser, products: productsRaw, setProducts, onOrderPlaced, cart, setCart, setPage, customer, paymentsPaused: paymentsPausedProp = false }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // ── Availability = physical stock − active checkout holds ──────────────────
  // Other shoppers' in-progress checkouts hold stock; subtract them so this store
  // shows true availability. Falls back to physical stock if the fetch fails.
  const [heldMap, setHeldMap] = useState(() => new Map());
  useEffect(() => {
    if (!storeUser?.id) return;
    let alive = true;
    const load = async () => {
      const rows = await api.heldStock(storeUser.id);
      if (alive) setHeldMap(buildHeldMap(rows));
    };
    load();
    const stopPoll = setVisibleInterval(load, 30000); // refresh holds every 30s (only while the tab is visible)
    return () => { alive = false; stopPoll(); };
  }, [storeUser?.id]);
  const products = useMemo(() => applyHolds(productsRaw, heldMap), [productsRaw, heldMap]);
  // Avg ratings for every product in one call → small ★ badge under card images.
  const avgRatings = useAvgRatings(useMemo(() => (productsRaw || []).map(p => p.id), [productsRaw]));
  // Treat the store as paused for checkout if EITHER the platform paused payments
  // OR this seller paused their own store (Settings → pause orders). This flows
  // through every existing "paymentsPaused" buy-button gate below automatically.
  const storeCheckoutPaused = !!storeUser?.checkout_paused;
  const paymentsPaused = paymentsPausedProp || storeCheckoutPaused;
  const [sel,          setSel]          = useState(null);   // selected product for detail modal
  const [selImgIdx,    setSelImgIdx]    = useState(0);      // which image is shown when modal opens
  const [qty,          setQty]          = useState(1);
  // MOP (Mode-of-Payment) for direct Buy Now — same pattern as Cart.jsx: UPI pre-selected;
  // disabled (mop.enabled false) means activeMethod is null and pricing/checkout behave
  // exactly like before this feature existed.
  const [payMethod,    setPayMethod]    = useState("upi");
  const mop = useMopConfig();
  const activeMethod = mop.enabled ? payMethod : null;
  const [selOpt,       setSelOpt]       = useState("");     // selected product option (legacy)
  const [selVar,       setSelVar]       = useState(null);   // selected variation {name, price, stock}
  // Variation-aware gallery for the quick-view swiper: a selected variation with its
  // own image shows on top; others fall back to the product's images. Memoised so the
  // ModalImageSwiper (which resets to frame 0 on array change) swaps on selection but
  // keeps swiping stable within a variation.
  const selGalleryImages = useMemo(() => {
    if (!sel) return [];
    const base = (Array.isArray(sel.images)&&sel.images.length>0) ? sel.images : (sel.img?[sel.img]:[]);
    const vimg = selVar && sel.variation_images?.[selVar.name];
    return vimg ? [vimg, ...base.filter(u=>u!==vimg)] : base;
  }, [sel, selVar]);
  const [selAdv,       setSelAdv]       = useState({colour:null, size:null, custom:null}); // advanced-variation picks
  // Reset advanced picks whenever the open product changes (or the modal closes).
  useEffect(()=>{ setSelAdv({colour:null, size:null, custom:null}); }, [sel?.id]);
  // A product that needs the customer to pick/answer something must open the detail modal —
  // never quick-buy from a card (would skip variation/combo/question/upload selection).
  const needsModal = (p) => !!(p.custom_question || p.require_upload
    || (Array.isArray(p.variations) && p.variations.length > 0)
    || advancedHasContent(p.advanced_variations));
  const [selCustomAns, setSelCustomAns] = useState("");     // customer's answer to product custom_question
  const [selCustomImgs, setSelCustomImgs] = useState([]);   // customer's uploaded image URLs (require_upload products; one per item)
  const [selImgUploading, setSelImgUploading] = useState(false);
  const [buying,    setBuying]    = useState(false);  // loading overlay while Razorpay initialises
  const [addrFor,     setAddrFor]     = useState(null);   // {p, q, opt} — pending payment waiting for address
  const [qbState,     setQbState]     = useState(null);   // quick-buy delivery state → per-state shipping
  const [sfSearch,    setSfSearch]    = useState("");
  const [sfColFilter, setSfColFilter] = useState("All"); // storefront collection filter
  const colLinkApplied = useRef(false); // the ?collection= link is read once, not again after the visitor picks a tab
  const [sfCollections, setSfCollections] = useState([]); // this store's collections
  const [sfAvailFirst, setSfAvailFirst] = useState(false); // sort available items before sold-out
  const [sfSort, setSfSort] = useState("default"); // default | price-asc | price-desc | newest | oldest
  const [visibleCount, setVisibleCount] = useState(24);  // pagination — show 24 at a time
  const [showAtcBar, setShowAtcBar] = useState(false);   // sticky ATC bar when buy btns scroll off screen
  const [restockProduct, setRestockProduct] = useState(null); // product to show restock modal for
  const atcButtonsRef = useRef(null);                     // ref on the buy-buttons div inside modal
  const pendingOrderIdRef = useRef(null);                 // last pending order, for retry-release

  // Load collections from storeUser.collections JSON field
  useEffect(()=>{
    if (!storeUser) return;
    const cols = Array.isArray(storeUser.collections) ? storeUser.collections
      : (typeof storeUser.collections === "string" ? (() => { try { return JSON.parse(storeUser.collections); } catch { return []; } })() : []);
    // Only show collections that actually have at least 1 product assigned
    const usedIds = new Set((products||[]).flatMap(p=>pColIds(p)));
    const shown = cols.filter(c => usedIds.has(c.id));
    setSfCollections(shown);
    // A shared collection link (/store/<slug>?collection=<id>) opens the store straight onto that collection — applied once.
    if (!colLinkApplied.current && shown.length) {
      colLinkApplied.current = true;
      const want = collectionFromSearch(window.location.search, shown.map(c => c.id));
      if (want) { setSfColFilter(want); setVisibleCount(24); }
    }
  },[storeUser, products]);

  // Picking a collection also puts it in the address bar, so the page's own URL is a shareable link to it.
  const pickCollection = (id) => {
    setSfColFilter(id); setVisibleCount(24);
    try { window.history.replaceState(window.history.state, "", window.location.pathname + withCollectionParam(window.location.search, id) + window.location.hash); } catch { /* non-critical */ }
  };

  // ── Preload Razorpay SDK on mount ─────────────────────────────────────────
  // Ensures checkout.js is ready for the FIRST "Buy Now" tap, including the grid
  // Buy-Now path that never opens the product modal. Without this the first payment
  // had to load the SDK cold inside Instagram's WebView (slow/flaky) — so the modal
  // "didn't open" until a second tap had it cached. Not infra cold-start (paid plan).
  useEffect(() => { loadRazorpaySDK().catch(()=>{}); }, []);
  // Also (re)preload when a product modal opens — harmless if already cached.
  useEffect(() => { if (sel) loadRazorpaySDK().catch(()=>{}); }, [sel]);

  // ── Sticky ATC bar — show when buy buttons scroll out of view ─────────────
  useEffect(() => {
    if (!sel) { setShowAtcBar(false); return; }
    const node = atcButtonsRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowAtcBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [sel]);

  // ── Redirect to login, saving current store URL ───────────────────────────
  // Uses localStorage (not sessionStorage) so the redirect survives Instagram's
  // iOS WebView wiping sessionStorage on cross-page navigation.
  const requireLogin = () => {
    localStorage.setItem("bloom_redirect", window.location.pathname);
    navigate("/customer/login");
  };

  const addCart = (p, qtyOverride) => {
    const addQty = qtyOverride ?? qty;
    if (paymentsPaused) { alert("This store has paused orders right now. Please check back soon!"); return; }
    // Require custom answer if the seller has set a question
    if (p.custom_question && !selCustomAns.trim()) {
      alert("Please answer the store's question before adding to cart.");
      return;
    }
    // Require uploaded image if the seller has enabled it
    if (p.require_upload && !selCustomImgs.length) {
      alert("Please upload the required image before adding to cart.");
      return;
    }
    // Each variation/combo gets its own cart slot — unique key = productId + variation/combo
    const cartKey = p.selectedVariation ? `${p.id}__${p.selectedVariation.name}` : p.selectedCombo ? `${p.id}__${p.selectedCombo}` : p.id;
    // Refuse a sold-out item here too — this function used to rely on the buttons being hidden, and `Number(p.stock||1)`
    // below turns a stock of 0 into 1. A real 0 (never a missing value) means sold out.
    if (p.selectedVariation ? (p.selectedVariation.stock != null && Number(p.selectedVariation.stock) <= 0) : (!p.selectedCombo && !p.selectedAdvanced && p.stock != null && Number(p.stock) <= 0)) { alert("Sorry, this item is sold out."); return; }   // advanced (per-size) products keep their stock on the size, not here
    const maxStock = p.selectedVariation ? Number(p.selectedVariation.stock||1) : Number(p.stock||1);
    // Always stamp user_id from the store context so cart grouping works even if the
    // product object was fetched without a user_id column (e.g. older API response).
    const baseItem = (p.user_id || storeUser?.id)
      ? { ...p, user_id: p.user_id || storeUser.id }
      : p;
    const itemToAdd = {
      ...baseItem,
      ...(p.custom_question ? { custom_question: p.custom_question, custom_answer: selCustomAns.trim() } : {}),
      ...(p.require_upload && selCustomImgs.length ? { custom_images: selCustomImgs, custom_image: selCustomImgs[0] } : {}),
    };
    setCart(c => {
      const e = c.find(x => (x.cartKey||x.id) === cartKey);
      return e
        ? c.map(x => (x.cartKey||x.id) === cartKey ? {...x, qty: Math.min(x.qty + addQty, maxStock)} : x)
        : [...c, {...itemToAdd, qty: Math.min(addQty, maxStock), cartKey}];
    });
    // Abandoned-cart signal for the seller's analytics (fire-and-forget).
    api.trackCart({ store_id: p.user_id || storeUser?.id, product_id: p.id, product_name: p.name,
      variation: p.selectedVariation?.name || p.selectedCombo || null, qty: addQty,
      customer_id: customer?.id || null, customer_name: customer?.name || null });
    setSel(null);
  };

  const remove = (cartKey) => setCart(c => c.filter(x => (x.cartKey||x.id) !== cartKey));
  const updQ   = (cartKey, qty) => setCart(c => c.map(x => {
    if ((x.cartKey||x.id) !== cartKey) return x;
    const maxStock = x.selectedVariation ? Number(x.selectedVariation.stock||1) : Number(x.stock||1);
    return {...x, qty: Math.min(Math.max(1, qty), maxStock)};
  }));

  // Step 1: show address modal
  const payWithCard = (p, q=1, opt="") => {
    // Guest checkout — no login required; the address modal collects buyer details.
    if (paymentsPaused) { alert("This store has paused orders right now. Please check back soon!"); return; }
    if (!storeUser || storeUser.id === DEMO_ID) { alert("Store not ready for payments."); return; }
    // Buy Now must not start for a sold-out item (the buttons are hidden for them, but this function didn't check). Only a real 0
    // counts; advanced (per-size) products keep stock on the size, and a chosen simple variation is checked by the modal.
    if (!opt && !p.selectedVariation && !p.selectedCombo && !p.selectedAdvanced && p.stock != null && Number(p.stock) <= 0) { alert("Sorry, this item is sold out."); return; }
    setAddrFor({ p, q, opt });
  };

  // Step 2: address + optional coupon confirmed → loading → Razorpay
  const proceedPayment = async (deliveryAddress, couponApplied = null, contact = null) => {
    const { p, q, opt } = addrFor;
    // Guest checkout: use the contact entered in the address form, falling back to the account.
    const cName  = (contact?.name  || customer?.name  || "Customer").trim();
    const cPhone = (contact?.phone || customer?.phone || "").trim();
    const cEmail = (contact?.email || customer?.email || "").trim() || null;
    setAddrFor(null);
    if (!storeUser?.has_upi) {
      alert("This store hasn't set up payments yet. Please contact the store owner.");
      return;
    }
    setBuying(true);
    // Retry-release: a previously dismissed attempt left its hold alive on purpose (UPI
    // can complete out-of-band). Starting a fresh attempt abandons it — release it so the
    // customer isn't blocked by their own stale hold. CAS-guarded backend = safe no-op.
    if (pendingOrderIdRef.current) {
      api.releaseStock(pendingOrderIdRef.current);
      pendingOrderIdRef.current = null;
    }
    const orderId = `BL-${Date.now().toString(36).toUpperCase()}`;
    // International order (delivery country ≠ India) → flat intl shipping instead of domestic.
    const international = !!contact?.international;
    // Buy-Now is a single item — free-shipping product → whole order ships free. Otherwise use
    // the per-state rate for the buyer's chosen state (if the store set one), else the common rate.
    const _sRate = qbState && storeUser?.state_shipping && storeUser.state_shipping[qbState] != null
      ? Number(storeUser.state_shipping[qbState]) : null;
    const _shipBase = _sRate != null ? _sRate : Number(storeUser.shipping_cost || 0);
    const shipping = international
      ? Number(storeUser.intl_shipping_cost || 0)
      : (p.free_shipping ? 0 : effectiveShipping(_shipBase, p.price * q, q, storeUser.free_ship_min_amount, storeUser.free_ship_min_qty, storeUser.ship_surplus_after_qty, storeUser.ship_surplus_amount));
    const itemWithOpt = opt ? {...p, selectedOption: opt} : p;

    const subtotal = p.price * q;
    const couponDiscount = (() => {
      if (!couponApplied) return 0;
      if (couponApplied.discount_type === "percent") return Math.round(subtotal * couponApplied.discount_value / 100);
      return Math.min(couponApplied.discount_value, subtotal);
    })();
    // Checkout add-ons ticked in the address step: resolved from the STORE'S settings (never the client's price) and added like shipping.
    const addonsPicked = resolveAddons(storeUser?.checkout_addons, contact?.addonIds);
    const addonsSum     = addonsTotal(addonsPicked);
    const addonsPayload = addonsPicked.map(a => ({ storeId: storeUser.id, id: a.id, price: a.price }));
    const productTotal  = Math.max(0, subtotal + shipping + addonsSum - couponDiscount);
    // Gateway gross-up + tiered platform fee — mirrors iOS Fees.swift byte-for-byte.
    // 'seller' → store absorbs both fees (tsf=0, excluded from the platform base; store
    // nets productTotal×0.9746). Default 'customer' → buyer pays both on top.
    const feeQuote      = quote([{ total: productTotal, ...feeFlags(storeUser) }], methodOpts(mop.methods, activeMethod));
    const grossAmount   = feeQuote.grossAmount;
    const txnFee        = feeQuote.gatewayFee;         // displayed gateway line
    const platformFee   = feeQuote.platformFeeCharged; // buyer-facing line (0 when absorbed)

    const pendingOrder = {
      id: orderId, store_id: storeUser.id,
      customer_name: cName,
      customer_phone: cPhone,
      customer_id: customer?.id || null,
      customer_email: cEmail,
      items: [{...itemWithOpt, qty:q}], amount: feeQuote.perStoreAmount[0], tsf: feeQuote.perStoreTsf[0],
      ...(addonsPicked.length ? { addons: addonsPicked.map(({ id, name, price, image }) => ({ id, name, price, image })) } : {}),   // the server re-stamps these from the store's settings
      // Platform fee: customer-pays → charged on top (amount full); absorb → taken out of
      // amount (seller bears it). Omitted when 0 (tier 0 / switch off).
      ...(feeQuote.perStorePlatformFee[0] > 0 ? { platform_fee: feeQuote.perStorePlatformFee[0] } : {}),
      // Who bore each fee — settlement reads these (backend re-stamps authoritatively from the
      // store's own settings; sending them keeps the pending row correct pre-confirm too).
      gw_absorbed: !!feeQuote.perStoreGwAbsorbed[0], pf_absorbed: !!feeQuote.perStorePfAbsorbed[0],
      status: "pending", proof: "", upi_ref: "", order_date: today(),
      delivery_address: deliveryAddress || null,  // saved NOW — before Razorpay opens
    };
    const inserted = await api.insertOrder(pendingOrder);
    if (!inserted) {
      alert("Could not create order — please try again.");
      setBuying(false); return;
    }
    pendingOrderIdRef.current = orderId;  // track for retry-release if the modal is dismissed

    // ── HOSTED redirect checkout (canary) — full-page navigation to Razorpay ──
    // Direct buy-now: don't wipe the whole cart (unrelated items are preserved), but DO
    // record the exact item bought so it's removed from the cart on return — otherwise a
    // customer who "Buy Now"s an item that's also in their cart sees it linger after paying.
    try {
      const vId = itemWithOpt.selectedVariation?.name || itemWithOpt.selectedOption || itemWithOpt.selectedCombo || itemWithOpt.variation_name || null;
      localStorage.setItem("bloom_purchased_items", JSON.stringify([{ id: itemWithOpt.id, variation: vId }]));
    } catch { /* ignore */ }
    if (isHostedPay()) {
      await startHostedRazorpayCheckout({
        amount: grossAmount, orderId, orderIds: [orderId],
        storeId: storeUser.id,
        storeName: storeUser.store_name,
        items: [{ ...itemWithOpt, qty: q }],
        coupon: couponApplied || null,
        addons: addonsPayload,
        international, payMethod: activeMethod,
        customerName:  cName,
        customerEmail: cEmail || "customer@bloom.store",
        customerPhone: cPhone || "9999999999",
        onError: (msg) => { setBuying(false); alert(msg); },
        onSoldOut: (list, info) => {
          setBuying(false);
          if (info?.retryable) { alert(info.message || "Couldn’t verify stock — please try again."); return; }
          const names = (list || []).map(u => u.name + (u.variation ? ` (${u.variation})` : "")).join(", ");
          alert(`Sorry — ${names || "this item"} just sold out.`);
        },
      });
      return; // page is navigating to Razorpay's hosted checkout
    }

    await startRazorpayPayment({
      amount: grossAmount, orderId,
      international, payMethod: activeMethod,
      storeId:   storeUser.id,
      customerId: customer?.id || null,
      customerName:  cName,
      customerEmail: cEmail || "customer@bloom.store",
      customerPhone: cPhone || "9999999999",
      storeName: storeUser.store_name, storeSlug: storeUser.store_slug,
      // storeEmail/storePhone intentionally NOT sent — the storefront no longer reads
      // the seller's email/phone (public_stores hides them). The backend (rzp-verify /
      // webhook) derives the seller's contact from the DB by storeId for notifications.
      ownerName:  storeUser.name,
      items: [{...itemWithOpt, qty:q}],
      deliveryAddress,
      shippingCost: shipping,
      coupon: couponApplied || null,
      addons: addonsPayload,
      onSuccess: async (pd) => {
        const confirmedOrder = { ...pendingOrder, status:"confirmed", upi_ref:String(pd?.paymentId||"") };
        onOrderPlaced(storeUser.id, { ...confirmedOrder, date:today() });
        // Immediately update local product/variation stock so sold-out shows without refresh
        if (setProducts) {
          setProducts(prev => prev.map(prod => {
            if (prod.id !== p.id) return prod;
            const varName = p.selectedVariation?.name || null;
            if (varName && Array.isArray(prod.variations) && prod.variations.length > 0) {
              const updatedVars = prod.variations.map(v =>
                v.name === varName ? { ...v, stock: Math.max(0, (v.stock||0) - q) } : v
              );
              const allOut = updatedVars.every(v => (v.stock||0) === 0);
              return { ...prod, variations: updatedVars, ...(allOut ? { stock: 0 } : {}) };
            }
            return { ...prod, stock: Math.max(0, (prod.stock||0) - q) };
          }));
        }
        setSel(null); setSelOpt("");
        pendingOrderIdRef.current = null;
        setBuying(false);
        setTimeout(() => window.location.reload(), 800);
      },
      onFail: async (msg, opts = {}) => {
        pendingOrderIdRef.current = null;
        await api.patchOrder(orderId, { status:"cancelled" });
        // Release strictly by order id — backend CAS-guards + reads items from the DB row.
        if (opts.stockReserved !== false) api.releaseStock(orderId);
        if (msg !== "Payment cancelled") alert(`Payment failed: ${msg}`);
        setBuying(false);
      },
      onDismiss: () => {
        // Modal closed without a result. Do NOT cancel/release — a UPI payment may still
        // land. Leave the order pending + hold alive (self-expires at the 30-min TTL, or
        // releases on retry). Keep pendingOrderIdRef for retry-release. Just reset the UI.
        setBuying(false);
      },
    });
  };

  if (!storeUser) return <div style={{padding:60,textAlign:"center",color:"var(--ink-soft)"}}>Store not found.</div>;

  const T = getTheme(storeUser?.template).T;
  const cardLayout = T.cardLayout || "default";

  return (
    <ThemeWrapper template={storeUser?.template} bgColor={storeUser?.bg_color} storeFont={storeUser?.store_font}>
    <div style={{minHeight:"100vh"}}>
      {/* ── Address collection modal (shown before Razorpay opens) ── */}
      {addrFor&&<AddressModal customer={customer} storeUser={storeUser} onConfirm={proceedPayment} onClose={()=>setAddrFor(null)} onStateChange={setQbState} couponSubtotal={Number(addrFor.p.price||0)*Number(addrFor.q||1)}
        shippingResolver={(country) => country === "India"
          ? { international: false, shipping: 0, unshippable: [] }
          : { international: true, shipping: Number(storeUser?.intl_shipping_cost || 0), unshippable: storeUser?.ships_international ? [] : [storeUser?.store_name || "This store"] }}/>}

      {/* ── Loading overlay while creating Razorpay order / loading SDK ── */}
      {buying&&(
        <div style={{position:"fixed",inset:0,background:"var(--overlay)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:400,gap:18}}>
          <div style={{width:52,height:52,border:"4px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
          <p style={{color:"#fff",fontFamily:"Syne",fontWeight:600,fontSize:15}}>Opening secure payment…</p>
        </div>
      )}

      {/* Store hero */}
      {(() => {
        const hasBanner = !!storeUser.store_banner;
        const tc = hasBanner ? "#fff" : T.headerText;
        const tcs = hasBanner ? "rgba(255,255,255,0.75)" : T.dark ? "rgba(255,255,255,0.55)" : "var(--ink-soft)";
        const badgeBg = hasBanner ? "rgba(255,255,255,0.18)" : T.dark ? "rgba(255,255,255,0.10)" : "var(--surface)";
        const badgeFg = hasBanner || T.dark ? "#fff" : "var(--text2)";
        return (
          <div className="storefront-hero" style={{
            background: hasBanner ? "transparent" : T.headerBg,
            backgroundImage: hasBanner && !isVideoUrl(storeUser.store_banner) ? `url(${storeUser.store_banner})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
            minHeight: hasBanner ? (isMobile?"360px":"420px") : undefined,
            padding: hasBanner ? (isMobile?"0 5vw 22px":"0 6vw 28px") : (isMobile?"36px 5vw":"56px 6vw"),
            display: "flex", alignItems: hasBanner?"flex-end":"center", gap: hasBanner?14:32, flexWrap: "wrap",
            position: "relative", overflow: "hidden",
          }}>
            {/* Video banner */}
            {hasBanner && isVideoUrl(storeUser.store_banner) && (
              <video src={videoUrl(storeUser.store_banner)} autoPlay muted loop playsInline preload="metadata" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",zIndex:0}}/>
            )}
            {/* Gradient scrim from bottom only — image shows fully at top */}
            {hasBanner && <div style={{position:"absolute",inset:0,background:"linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.10) 55%, transparent 100%)",zIndex:1}}/>}
            {/* Decorative gradient orb (theme-only, hidden when banner present) */}
            {!hasBanner && T.headerBg.includes("gradient") && <div style={{position:"absolute",right:30,top:-30,width:160,height:160,borderRadius:"50%",background:"rgba(255,255,255,0.25)",filter:"blur(35px)",pointerEvents:"none"}}/>}
            {/* All content in a single wrapper so z-index:2 covers the scrim */}
            <div style={{position:"relative",zIndex:2,display:"flex",alignItems:"center",gap:hasBanner?12:32,flexWrap:"wrap",width:"100%"}}>
              {storeUser.store_logo_video
                ? <video src={videoUrl(storeUser.store_logo_video)} poster={storeUser.store_image||undefined} autoPlay muted loop playsInline preload="metadata" style={{width:isMobile?(hasBanner?52:64):(hasBanner?72:88),height:isMobile?(hasBanner?52:64):(hasBanner?72:88),borderRadius:T.radius==="0px"?"0":4,objectFit:"cover",border:hasBanner?"2px solid rgba(255,255,255,0.7)":"1px solid var(--border)",flexShrink:0,boxShadow:hasBanner?"0 2px 14px rgba(0,0,0,0.5)":"none"}}/>
                : storeUser.store_image
                ? <img src={storeUser.store_image} alt={storeUser.store_name} style={{width:isMobile?(hasBanner?52:64):(hasBanner?72:88),height:isMobile?(hasBanner?52:64):(hasBanner?72:88),borderRadius:T.radius==="0px"?"0":4,objectFit:"cover",border:hasBanner?"2px solid rgba(255,255,255,0.7)":"1px solid var(--border)",flexShrink:0,boxShadow:hasBanner?"0 2px 14px rgba(0,0,0,0.5)":"none"}}/>
                : <Monogram name={storeUser.store_name} size={isMobile?56:84}/>
              }
              <div style={{flex:1,minWidth:140}}>
                <h1 style={{fontSize:hasBanner?(isMobile?"clamp(20px,5vw,30px)":"clamp(24px,3.5vw,40px)"):"clamp(26px,4vw,40px)",color:tc,marginBottom:0,textShadow:hasBanner?"0 1px 8px rgba(0,0,0,0.6)":"none"}}>{storeUser.store_name}</h1>
                <p style={{color:tcs,fontSize:hasBanner?10:14,marginTop:hasBanner?2:6,textShadow:hasBanner?"0 1px 4px rgba(0,0,0,0.5)":"none"}}>{storeUser.store_desc||"Handpicked with love · Ships across India"}</p>
                <div style={{display:"flex",gap:hasBanner?4:6,marginTop:hasBanner?6:12,flexWrap:"wrap",alignItems:"center"}}>
                  {/* Badge sizing: tiny when banner active, normal otherwise */}
                  {(() => {
                    const bp = hasBanner ? "1px 7px" : "3px 11px";
                    const bfs = hasBanner ? 9 : 11;
                    const bfw = hasBanner ? 600 : 700;
                    return (<>
                      <span className="bdg-green" style={{borderRadius:T.badgeRadius,padding:bp,fontSize:bfs,fontWeight:bfw,...(hasBanner?{background:"rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.9)",border:"1px solid rgba(255,255,255,0.25)"}:{background:badgeBg,color:badgeFg,border:"none"})}}><Icon n="done" size="1.1em" inline /> Verified Seller</span>
                      {storeUser.has_upi&&<span style={{background:hasBanner?"rgba(255,255,255,0.15)":badgeBg,color:hasBanner?"rgba(255,255,255,0.9)":badgeFg,borderRadius:T.badgeRadius,padding:bp,fontSize:bfs,fontWeight:bfw}}><Icon n="mobile" size="1.1em" inline /> UPI Payments</span>}
                      {storeUser.dispatch_days&&<span style={{background:hasBanner?"rgba(255,255,255,0.15)":badgeBg,color:hasBanner?"rgba(255,255,255,0.9)":badgeFg,borderRadius:T.badgeRadius,padding:bp,fontSize:bfs,fontWeight:bfw}}><Icon n="truck" size="1.1em" inline /> Ships in {storeUser.dispatch_days}</span>}
                      {storeUser.shipping_cost>0&&<span style={{background:hasBanner?"rgba(255,255,255,0.15)":badgeBg,color:hasBanner?"rgba(255,255,255,0.9)":badgeFg,borderRadius:T.badgeRadius,padding:bp,fontSize:bfs,fontWeight:bfw}}><Icon n="package" size="1.1em" inline /> ₹{storeUser.shipping_cost} shipping</span>}
                      {storeUser.shipping_cost===0&&storeUser.dispatch_days&&<span style={{background:hasBanner?"rgba(255,255,255,0.15)":badgeBg,color:hasBanner?"rgba(255,255,255,0.9)":badgeFg,borderRadius:T.badgeRadius,padding:bp,fontSize:bfs,fontWeight:bfw}}><Icon n="sparkle" size="1.1em" inline /> Free Shipping</span>}
                      {storeUser.instagram&&<InstagramChip handle={storeUser.instagram} onBanner={hasBanner} radius={parseInt(T.badgeRadius)||4}/>}
                    </>);
                  })()}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* The store's own live offers (BOGO, bundle, bulk tiers, free shipping) as colour-blocked cards — only what the seller has switched on */}
      <StoreOffers store={storeUser} isMobile={isMobile} />

      {!storeUser.has_upi&&<div style={{margin:"20px 6vw 0",background:"var(--warn-bg)",border:"1px solid var(--yellow-deep)",borderRadius:4,padding:"12px 18px",fontSize:13,color:"var(--warn-fg)",fontWeight:600}}><Icon n="warning" size="1.1em" inline /> Store is not accepting payments yet.</div>}

      {/* Products grid */}
      <div style={{padding:cardLayout==="reformation"?"0":(isMobile?"24px 4vw":"44px 6vw")}}>
        {/* ── Heading row ── */}
        {cardLayout==="reformation" ? (
          <div className="storefront-hero" style={{padding:isMobile?"28px 4vw 20px":"44px 6vw 32px",borderBottom:"1px solid var(--border)",marginBottom:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",gap:16,flexWrap:"wrap"}}>
              <h2 style={{fontFamily:"'Instrument Serif',Georgia,serif",textTransform:"uppercase",lineHeight:.95,fontSize:isMobile?"32px":"clamp(34px,5vw,58px)",fontWeight:400,letterSpacing:"-0.015em",margin:0}}>
                Shop the <em>Collection</em>
              </h2>
              {products.length>0&&(
                <div style={{position:"relative",width:isMobile?"100%":240}}>
                  <input className="inp" placeholder="Search products…" value={sfSearch} onChange={e=>{setSfSearch(e.target.value);setVisibleCount(24);}} style={{paddingLeft:36,fontSize:12,width:"100%"}}/>
                  <div style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}><Icon n="search" size={14} color="var(--ink-soft)"/></div>
                </div>
              )}
            </div>
          </div>
        ) : cardLayout==="dark" ? (
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:sfCollections.length>0?16:0}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <h2 style={{fontSize:isMobile?22:28,margin:0}}>New arrivals</h2>
              </div>
              {products.length>0&&(
                <div style={{position:"relative",width:isMobile?"100%":220}}>
                  <input className="inp" placeholder="Search…" value={sfSearch} onChange={e=>{setSfSearch(e.target.value);setVisibleCount(24);}} style={{paddingLeft:36,fontSize:12,width:"100%"}}/>
                  <div style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}><Icon n="search" size={14} color="var(--ink-soft)"/></div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:24}}>
            <h2 style={{fontSize:isMobile?22:28}}>Products <span style={{color:"var(--ink-soft)",fontWeight:400,fontSize:16}}>({products.filter(x=>x.is_live!==false).length})</span></h2>
            {products.length>0&&(
              <div style={{position:"relative",width:isMobile?"100%":260}}>
                <input className="inp" placeholder="Search products…" value={sfSearch} onChange={e=>{setSfSearch(e.target.value);setVisibleCount(24);}} style={{paddingLeft:40,width:"100%"}}/>
                <div style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}><Icon n="search" size={15} color="var(--ink-soft)"/></div>
              </div>
            )}
          </div>
        )}
        {/* ── Collection filter bar ── */}
        {sfCollections.length>0&&(
          <div style={{display:"flex",gap:cardLayout==="dark"?20:8,marginBottom:cardLayout==="dark"?16:24,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",paddingBottom:2,
            borderBottom:cardLayout==="dark"?"1px solid var(--border)":"none",paddingTop:cardLayout==="dark"?0:undefined}}>
            {[{id:"All",name:"All"},...sfCollections].map(c=>{
              const active = sfColFilter===c.id;
              if (cardLayout==="dark") return (
                <button key={c.id} onClick={()=>pickCollection(c.id)}
                  style={{background:"none",border:"none",borderBottom:active?"2px solid var(--ink)":"2px solid transparent",padding:"4px 0",paddingBottom:10,fontSize:14,fontWeight:active?600:500,cursor:"pointer",color:active?"var(--ink)":"var(--ink-soft)",transition:"all 0.15s",whiteSpace:"nowrap",fontFamily:"inherit"}}>
                  {c.id==="All"?"All":c.name}
                </button>
              );
              if (cardLayout==="reformation") return (
                <button key={c.id} onClick={()=>pickCollection(c.id)}
                  style={{background:"none",border:"none",borderBottom:active?"1.5px solid #000":"1.5px solid transparent",padding:"4px 0",fontSize:10,fontWeight:active?600:400,letterSpacing:"0.08em",cursor:"pointer",color:active?"#000":"rgba(0,0,0,0.38)",textTransform:"uppercase",transition:"all 0.15s",whiteSpace:"nowrap",fontFamily:"'Syne',sans-serif"}}>
                  {c.id==="All"?"ALL":c.name.toUpperCase()}
                </button>
              );
              return (
                <button key={c.id} onClick={()=>pickCollection(c.id)}
                  style={{background:active?"var(--pink-btn)":"var(--white)",color:active?"var(--on-accent)":"var(--ink)",border:`1px solid ${active?"var(--pink-btn)":"var(--border-mid)"}`,borderRadius:4,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.18s",flexShrink:0}}>
                  {c.id==="All"?"All":c.name}
                </button>
              );
            })}
          </div>
        )}
        {/* Reformation collection filters (when no collections, still needs padding) */}
        {cardLayout==="reformation"&&sfCollections.length===0&&<div style={{height:24}}/>}

        {/* ── Availability sort toggle ── */}
        <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:8,marginBottom:12,marginTop:sfCollections.length>0?-12:0}}>
          <select value={sfSort} onChange={e=>{setSfSort(e.target.value);setVisibleCount(24);}}
            style={{padding:"7px 12px",borderRadius:cardLayout==="reformation"?0:4,border:"1px solid var(--border-mid)",background:"var(--white)",color:"var(--ink)",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"inherit",outline:"none"}}>
            <option value="default">Sort: Featured</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          {cardLayout==="dark" ? (
            <button onClick={()=>setSfAvailFirst(v=>!v)} style={{display:"flex",alignItems:"center",gap:6,background:sfAvailFirst?"var(--teal-light)":"var(--white)",color:sfAvailFirst?"var(--ink)":"var(--ink-soft)",border:`1px solid ${sfAvailFirst?"var(--ink)":"var(--border-mid)"}`,borderRadius:4,padding:"7px 14px",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s",whiteSpace:"nowrap"}}>
              <Icon n="sparkle" size={14} inline /> Available first
            </button>
          ) : cardLayout==="reformation" ? (
            <button onClick={()=>setSfAvailFirst(v=>!v)} style={{display:"flex",alignItems:"center",gap:6,background:"none",color:sfAvailFirst?"#000":"rgba(0,0,0,0.38)",border:"none",borderBottom:sfAvailFirst?"1.5px solid #000":"1.5px solid transparent",padding:"4px 0",fontSize:10,fontWeight:sfAvailFirst?600:400,letterSpacing:"0.08em",cursor:"pointer",textTransform:"uppercase",transition:"all 0.15s",whiteSpace:"nowrap"}}>
              <Icon n="sparkle" size="1em" inline /> AVAILABLE FIRST
            </button>
          ) : (
            <button onClick={()=>setSfAvailFirst(v=>!v)} style={{display:"flex",alignItems:"center",gap:6,background:sfAvailFirst?"var(--teal-light)":"var(--surface)",color:sfAvailFirst?"var(--teal-deep)":"var(--ink-soft)",border:`1.5px solid ${sfAvailFirst?"var(--teal)":"var(--border-mid)"}`,borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all 0.18s"}}>
              <Icon n="sparkle" size={14} inline /> Available first
            </button>
          )}
        </div>

        {products.length===0
          ? <p style={{color:"var(--ink-soft)",textAlign:"center",padding:"40px 0"}}>No products listed yet.</p>
          : <div style={{display:"grid",gridTemplateColumns:
              cardLayout==="reformation"?(isMobile?"repeat(2,1fr)":"repeat(4,1fr)"):
              cardLayout==="dark"?(isMobile?"repeat(2,1fr)":"repeat(3,1fr)"):
              `repeat(auto-fill,minmax(${isMobile?155:200}px,1fr))`,
              gap:cardLayout==="reformation"?(isMobile?2:4):cardLayout==="dark"?(isMobile?8:12):(isMobile?12:20)}}>
              {(()=>{
                // Sort: pinned first, then custom order / newest-first, then optionally available-before-sold-out
                let sorted;
                try {
                  // ONE store-wide order: a collection shows the same sequence as "All", filtered to its products.
                  const order = storeUser?.product_order ? JSON.parse(storeUser.product_order) : null;
                  if (order && order.length) {
                    const pos = Object.fromEntries(order.map((id,i)=>[id,i]));
                    sorted = [...products].sort((a,b)=>{
                      const ap=a.is_pinned?0:1, bp=b.is_pinned?0:1;
                      if (ap!==bp) return ap-bp;
                      const ai=pos[a.id]??-1, bi=pos[b.id]??-1;
                      return ai!==bi ? ai-bi : new Date(b.created_at||0)-new Date(a.created_at||0);
                    });
                  } else {
                    sorted = [...products].sort((a,b)=>{
                      const ap=a.is_pinned?0:1, bp=b.is_pinned?0:1;
                      if (ap!==bp) return ap-bp;
                      return new Date(b.created_at||0)-new Date(a.created_at||0);
                    });
                  }
                } catch { sorted = [...products].sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0)); }
                // Explicit customer sort overrides the featured/pinned ordering
                if (sfSort === "price-asc")  sorted = [...products].sort((a,b)=>(Number(a.price)||0)-(Number(b.price)||0));
                else if (sfSort === "price-desc") sorted = [...products].sort((a,b)=>(Number(b.price)||0)-(Number(a.price)||0));
                else if (sfSort === "newest") sorted = [...products].sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
                else if (sfSort === "oldest") sorted = [...products].sort((a,b)=>new Date(a.created_at||0)-new Date(b.created_at||0));
                if (sfAvailFirst) {
                  sorted = sorted.sort((a,b)=>{
                    const as=Number(a.stock||0)===0?1:0, bs=Number(b.stock||0)===0?1:0;
                    return as-bs;
                  });
                }
                return sorted;
              })()
                .filter(p=>p.is_live!==false&&(sfColFilter==="All"||pInCol(p,sfColFilter))&&(!sfSearch||(p.name||"").toLowerCase().includes(sfSearch.toLowerCase())))
                .slice(0, visibleCount)
                .map((p,i)=>{
                const soldOut = Number(p.stock||0)===0;
                const hasVars = (Array.isArray(p.variations)&&p.variations.length>0) || advancedHasContent(p.advanced_variations);
                const cardItem = cart.find(x=>(x.cartKey||x.id)===p.id);
                const cardQty = cardItem?.qty||0;
                const cardMax = Number(p.stock||0);
                const cardMore = cardQty<cardMax;

                const cardButtons = soldOut
                  ? <SoldOutLabel block onRestock={storeUser?.restock_enabled !== false ? ()=>setRestockProduct(p) : null} />
                  : <div onClick={e=>e.stopPropagation()}>
                      <div style={{display:"flex",gap:6}}>
                        {hasVars
                          ? <button className="btn-pink" style={{flex:1,padding:"8px 4px",fontSize:12,whiteSpace:"nowrap",minWidth:0}} onClick={()=>{setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}}>View options</button>
                          : cardItem
                            ? <>
                                <div onClick={e=>e.stopPropagation()} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--pink-btn)",borderRadius:4,padding:"0 4px",height:34}}>
                                  <button onClick={e=>{e.stopPropagation();cardQty<=1?remove(p.id):updQ(p.id,cardQty-1);}}
                                    style={{background:"rgba(127,127,127,0.25)",border:"none",width:32,height:32,borderRadius:4,fontSize:16,cursor:"pointer",color:"var(--on-accent)",fontWeight:700}}>−</button>
                                  <span style={{fontWeight:700,fontSize:13,color:"var(--on-accent)"}}>{cardQty}</span>
                                  <button onClick={e=>{e.stopPropagation();if(cardMore)updQ(p.id,cardQty+1);}} disabled={!cardMore}
                                    style={{background:cardMore?"rgba(127,127,127,0.25)":"transparent",border:"none",width:32,height:32,borderRadius:4,fontSize:16,cursor:cardMore?"pointer":"not-allowed",color:"var(--on-accent)",fontWeight:700,opacity:cardMore?1:0.3}}>+</button>
                                </div>
                                {paymentsPaused
                                  ? <button disabled style={{flex:1,padding:"8px 10px",fontSize:11,border:"none",background:"rgba(0,0,0,0.08)",color:"var(--ink-soft)",cursor:"not-allowed",borderRadius:4,fontWeight:600}}><Icon n="locked" size="1.1em" inline /> Payments paused</button>
                                  : <button className="btn-pink" style={{flex:1,padding:"8px 4px",fontSize:12,whiteSpace:"nowrap",minWidth:0}} onClick={e=>{e.stopPropagation();if(needsModal(p)){setSel(p);setQty(cardQty);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else payWithCard(p,cardQty);}}>Buy now</button>
                                }
                              </>
                            : <>
                                {!paymentsPaused && <button className="btn-ghost" style={{flex:1,padding:"8px 4px",fontSize:12,whiteSpace:"nowrap",minWidth:0}} onClick={e=>{e.stopPropagation();if(needsModal(p)){setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else addCart(p);}}>Add to cart</button>}
                                {paymentsPaused
                                  ? <button disabled style={{flex:1,padding:"8px 10px",fontSize:11,border:"none",background:"rgba(0,0,0,0.08)",color:"var(--ink-soft)",cursor:"not-allowed",borderRadius:4,fontWeight:600}}><Icon n="locked" size="1.1em" inline /> Payments paused</button>
                                  : <button className="btn-pink" style={{flex:1,padding:"8px 4px",fontSize:12,whiteSpace:"nowrap",minWidth:0}} onClick={e=>{e.stopPropagation();if(needsModal(p)){setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else payWithCard(p,1);}}>Buy now</button>
                                }
                              </>
                        }
                      </div>
                    </div>;

                // ── DARK layout (Black template) ────────────────────────────
                if (cardLayout==="dark") {
                  const hasDiscount = p.mrp && Number(p.mrp) > Number(p.price);
                  const cardImages = Array.isArray(p.images)&&p.images.length>0 ? p.images : (p.img?[p.img]:[]);
                  return (
                  <DarkCardWithCarousel key={p.id} p={p} i={i} isMobile={isMobile} soldOut={soldOut} hasDiscount={hasDiscount} bogoBadge={p.bogo_eligible ? bogoLabel(storeUser) : null}
                    hasVars={hasVars} cardItem={cardItem} cardQty={cardQty} cardMore={cardMore} cardImages={cardImages}
                    onOpen={()=>{setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}}
                    onRemove={()=>{ cardQty<=1?remove(p.id):updQ(p.id,cardQty-1); }}
                    onAdd={()=>updQ(p.id,cardQty+1)}
                    onCart={()=>{ if(needsModal(p)){setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else addCart(p); }}
                    onBuy={()=>{ if(needsModal(p)){setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else payWithCard(p,1); }}
                    onRestockRequest={storeUser?.restock_enabled !== false ? ()=>setRestockProduct(p) : null}
                    paymentsPaused={paymentsPaused}
                    rating={avgRatings[p.id]}
                  />
                  );
                }

                // ── REFORMATION layout (Neutral) ────────────────────────────
                if (cardLayout==="reformation") {
                  const hasDiscount = p.mrp && Number(p.mrp) > Number(p.price);
                  const discPct = hasDiscount ? Math.round((Number(p.mrp)-Number(p.price))/Number(p.mrp)*100) : 0;
                  const cardImages = Array.isArray(p.images)&&p.images.length>0 ? p.images : (p.img?[p.img]:[]);
                  const openCard = () => { setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]); };
                  return (
                  <div key={p.id} className="fadeUp ref-card"
                    style={{cursor:"pointer",background:"var(--white)",animation:`fadeUp 0.4s ease ${i*0.05}s both`}}
                    onClick={openCard}>
                    {/* Portrait image with carousel */}
                    <div style={{aspectRatio:"2/3",overflow:"hidden",position:"relative",background:"var(--surface)"}}>
                      <CardCarousel images={cardImages.length>0?cardImages:[""]} style={{width:"100%",height:"100%"}} onClick={openCard} soldOut={soldOut} badge={p.bogo_eligible ? bogoLabel(storeUser) : null}>
                        {(src) => src
                          ? <SafeImg src={thumbUrl(src,600)} fallback={src} alt={p.name} loading="lazy" className="ref-img"
                              style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                          : <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,background:"var(--surface)"}}>
                              <span style={{fontSize:28,opacity:0.3}}><Icon n="camera" size={28} inline /></span>
                              <span style={{fontSize:9,color:"var(--ink-soft)",letterSpacing:"0.1em",textTransform:"uppercase"}}>No photo</span>
                            </div>
                        }
                      </CardCarousel>
                      {/* Image badges */}
                      <div style={{position:"absolute",top:10,left:10,display:"flex",flexDirection:"column",gap:5,pointerEvents:"none",zIndex:3}}>
                        {p.is_pinned&&!soldOut&&<span style={{background:"#000",color:"#fff",fontSize:9,fontWeight:600,letterSpacing:"0.1em",padding:"4px 9px"}}>FEATURED</span>}
                        {hasDiscount&&!soldOut&&<span style={{background:"#B42318",color:"#fff",fontSize:9,fontWeight:600,letterSpacing:"0.1em",padding:"4px 9px"}}>SALE</span>}
                      </div>
                    </div>
                    {/* Text info */}
                    <div style={{padding:isMobile?"10px 6px 14px":"12px 4px 18px"}}>
                      <RatingBadge rating={avgRatings[p.id]} style={{marginBottom:5}}/>
                      <div className="sf-pname" style={{fontSize:isMobile?12:13,fontWeight:500,color:"var(--ink)",marginBottom:5,lineHeight:1.35,letterSpacing:"0.01em"}}>{p.name}</div>
                      <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:10}}>
                        <span style={{fontSize:isMobile?13:14,fontWeight:500,color:"var(--ink)"}}>₹{(Number(p.price)||0).toLocaleString()}</span>
                        {hasDiscount&&<span style={{fontSize:11,color:"rgba(0,0,0,0.35)",textDecoration:"line-through"}}>₹{Number(p.mrp).toLocaleString()}</span>}
                        {hasDiscount&&<span style={{fontSize:10,color:"var(--red)",fontWeight:600}}>{discPct}% off</span>}
                      </div>
                      {/* BOGO shows as a corner ribbon on the image (CardCarousel
                          `badge`) — no second text badge here, it just crowds the card. */}
                      {/* Actions */}
                      {soldOut
                        ? <SoldOutLabel block height={34} fontSize={11} onRestock={storeUser?.restock_enabled !== false ? ()=>setRestockProduct(p) : null} />
                        : <div onClick={e=>e.stopPropagation()}>
                          {hasVars
                            ? <button onClick={openCard}
                                style={{width:"100%",padding:"9px 0",fontSize:10,letterSpacing:"0.1em",fontWeight:600,background:"#000",color:"#fff",border:"none",cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>VIEW OPTIONS</button>
                            : cardItem
                              ? <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",border:"1px solid #000",padding:"0 12px",height:34}}>
                                  <button onClick={e=>{e.stopPropagation();cardQty<=1?remove(p.id):updQ(p.id,cardQty-1);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,fontWeight:300,color:"var(--ink)",lineHeight:1}}>−</button>
                                  <span style={{fontSize:11,fontWeight:600,letterSpacing:"0.08em",fontFamily:"'Syne',sans-serif"}}>{cardQty} IN BAG</span>
                                  <button onClick={e=>{e.stopPropagation();if(cardMore)updQ(p.id,cardQty+1);}} disabled={!cardMore} style={{background:"none",border:"none",cursor:cardMore?"pointer":"not-allowed",fontSize:18,fontWeight:300,color:cardMore?"#000":"rgba(0,0,0,0.2)",lineHeight:1}}>+</button>
                                </div>
                              : <div style={{display:"flex",gap:3}}>
                                  {!paymentsPaused && <button onClick={e=>{e.stopPropagation();if(needsModal(p)){setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else addCart(p);}}
                                    style={{flex:1,padding:"9px 0",fontSize:10,letterSpacing:"0.08em",fontWeight:600,background:"var(--white)",color:"var(--ink)",border:"1px solid rgba(0,0,0,0.8)",cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>+ CART</button>}
                                  {paymentsPaused
                                    ? <button disabled style={{flex:1,padding:"9px 0",fontSize:10,fontWeight:600,background:"rgba(0,0,0,0.08)",color:"var(--ink-soft)",border:"none",cursor:"not-allowed",borderRadius:4}}><Icon n="locked" size="1.1em" inline /> Payments paused</button>
                                    : <button onClick={e=>{e.stopPropagation();if(needsModal(p)){setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);}else payWithCard(p,1);}}
                                        style={{flex:1,padding:"9px 0",fontSize:10,letterSpacing:"0.08em",fontWeight:600,background:"#000",color:"#fff",border:"none",cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>BUY NOW</button>
                                  }
                                </div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                  );
                }

                // ── EDITORIAL layout (Neutral) ──────────────────────────────
                if (cardLayout==="editorial") {
                  const editorialImages = Array.isArray(p.images)&&p.images.length>0 ? p.images : (p.img?[p.img]:[]);
                  const openCard = () => { setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]); };
                  return (
                  <div key={p.id} className="fadeUp prod-card"
                    style={{overflow:"hidden",cursor:"pointer",background:"var(--white)",border:"1px solid var(--border)",animation:`fadeUp 0.45s ease ${i*0.06}s both`,transition:"box-shadow 0.25s,transform 0.25s"}}
                    onClick={openCard}>
                    <div style={{position:"relative",overflow:"hidden",height:isMobile?200:280}}>
                      {editorialImages.length > 0
                        ? <CardCarousel images={editorialImages} style={{width:"100%",height:"100%"}} onClick={openCard} soldOut={soldOut} badge={p.bogo_eligible ? bogoLabel(storeUser) : null}>
                            {(src) => isVideoUrl(src)
                              ? <AutoVideo src={videoUrl(src)} poster={!isVideoUrl(posterOf(p)) ? thumbUrl(posterOf(p),400) : undefined} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                              : <img src={thumbUrl(src,400)} alt={p.name} loading="lazy"
                                  style={{width:"100%",height:"100%",objectFit:"cover",display:"block",transition:"transform 0.5s ease"}}/>
                            }
                          </CardCarousel>
                        : <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,background:"var(--surface)"}}>
                            <span style={{fontSize:28,opacity:0.3}}><Icon n="camera" size={28} inline /></span>
                            <span style={{fontSize:9,color:"var(--ink-soft)",letterSpacing:"0.1em",textTransform:"uppercase"}}>No photo</span>
                          </div>
                      }
                    </div>
                    <div style={{padding:"14px 16px 18px",borderTop:"1px solid var(--border)"}}>
                      <RatingBadge rating={avgRatings[p.id]} style={{marginBottom:6}}/>
                      <h4 style={{fontSize:isMobile?12:13,fontWeight:400,marginBottom:6,color:"var(--ink)",letterSpacing:"0.01em",lineHeight:1.4}}>{p.name}</h4>
                      <div style={{marginBottom:10,display:"flex",alignItems:"baseline",gap:8}}>
                        <span style={{fontSize:isMobile?17:19,fontWeight:700,color:"var(--ink)"}}>₹{(Number(p.price)||0).toLocaleString()}</span>
                        {p.mrp&&Number(p.mrp)>Number(p.price)&&<>
                          <span style={{fontSize:11,color:"rgba(17,17,17,0.38)",textDecoration:"line-through"}}>₹{Number(p.mrp).toLocaleString()}</span>
                          <span style={{fontSize:10,color:"var(--ink)",fontWeight:600,letterSpacing:"0.06em"}}>{Math.round((Number(p.mrp)-Number(p.price))/Number(p.mrp)*100)}% OFF</span>
                        </>}
                      </div>
                      {cardButtons}
                    </div>
                  </div>
                  );
                }

                // ── DEFAULT layout (Bloom): the same tile as Discover — one add-to-bag button; Buy now lives in the product view ──
                const tileProduct = { ...p, _storeInfo: storeUser, checkout_paused: paymentsPaused };
                const openDefault = () => {
                  if (soldOut) { if (storeUser?.restock_enabled !== false) setRestockProduct(p); return; }
                  setSel(p);setQty(1);setSelVar(null);setSelImgIdx(0);setSelCustomAns("");setSelCustomImgs([]);
                };
                return (
                  <ProductTile key={p.id} product={tileProduct} hideStore rating={avgRatings[p.id]} added={!!cardItem}
                    onOpen={openDefault} footer={cardButtons} />
                );
              })}
            </div>
        }
      </div>

      {/* ── Load More button ── */}
      {(()=>{
        const totalFiltered = (()=>{
          try {
            const order = storeUser?.product_order ? JSON.parse(storeUser.product_order) : null;
            let sorted;
            if (order && order.length) {
              const pos = Object.fromEntries(order.map((id,i)=>[id,i]));
              sorted = [...products].sort((a,b)=>{ const ap=a.is_pinned?0:1,bp=b.is_pinned?0:1; if(ap!==bp)return ap-bp; const ai=pos[a.id]??-1,bi=pos[b.id]??-1; return ai!==bi?ai-bi:new Date(b.created_at||0)-new Date(a.created_at||0); });
            } else {
              sorted = [...products].sort((a,b)=>{ const ap=a.is_pinned?0:1,bp=b.is_pinned?0:1; if(ap!==bp)return ap-bp; return new Date(b.created_at||0)-new Date(a.created_at||0); });
            }
            return sorted.filter(p=>(sfColFilter==="All"||pInCol(p,sfColFilter))&&(!sfSearch||(p.name||"").toLowerCase().includes(sfSearch.toLowerCase()))).length;
          } catch { return products.length; }
        })();
        if (totalFiltered <= visibleCount) return null;
        return (
          <div style={{textAlign:"center",marginTop:28,marginBottom:8}}>
            <button onClick={()=>setVisibleCount(v=>v+24)}
              style={{background:"var(--white)",border:"2px solid var(--teal)",color:"var(--teal)",borderRadius:4,padding:"12px 36px",fontSize:14,fontWeight:700,cursor:"pointer",transition:"all 0.18s"}}
              onMouseEnter={e=>{e.currentTarget.style.background="var(--teal)";e.currentTarget.style.color="var(--on-accent)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="var(--white)";e.currentTarget.style.color="var(--teal)";}}>
              Load More ({totalFiltered - visibleCount} more)
            </button>
          </div>
        );
      })()}

      {/* ── Product Detail Modal ── */}
      {sel&&(()=>{
        const selImages = (Array.isArray(sel.images)&&sel.images.length>0) ? sel.images : (sel.img?[sel.img]:[]);
        const hasVariations = Array.isArray(sel.variations) && sel.variations.length > 0;
        // ── Advanced variations (colour / size / custom) — mutually exclusive with the simple list ──
        const adv = sel.advanced_variations;
        const hasAdvanced = !hasVariations && advancedHasContent(adv);
        const advGroups = hasAdvanced ? [
          (adv.colour&&adv.colour.length)      ? {key:"colour", label:"COLOUR",                       values:adv.colour} : null,
          (adv.size&&adv.size.length)          ? {key:"size",   label:"SIZE", isSize:true,            values:adv.size}   : null,
          (adv.custom&&adv.custom.values&&adv.custom.values.length) ? {key:"custom", label:(adv.custom.label||"OPTION").toUpperCase(), values:adv.custom.values} : null,
        ].filter(Boolean) : [];
        const advAllChosen = advGroups.every(g => selAdv[g.key]);
        const advAddon = advGroups.reduce((s,g)=>{ const v=g.values.find(x=>x.name===selAdv[g.key]); return s + (v?Number(v.price||0):0); }, 0);
        const selSizeRow = (hasAdvanced && adv.size) ? adv.size.find(x=>x.name===selAdv.size) : null;
        const advSizeSoldOut = !!(selSizeRow && selSizeRow.stock!=null && Number(selSizeRow.stock)===0);
        const advComboLabel = advGroups.map(g=>selAdv[g.key]).filter(Boolean).join(" / ");
        // Unified "needs a pick before buying" flags so the simple + advanced paths share gating.
        const needsSelection = hasVariations || hasAdvanced;
        const selectionMade  = hasVariations ? !!selVar : hasAdvanced ? advAllChosen : true;

        const activePrice = selVar ? selVar.price : (hasAdvanced ? (Number(sel.price)||0)+advAddon : sel.price);
        const activeStock = selVar ? selVar.stock
          : (hasAdvanced && selSizeRow && selSizeRow.stock!=null ? Math.min(Number(sel.stock||0), Number(selSizeRow.stock)) : Number(sel.stock||0));
        const soldOut = hasVariations ? (selVar ? Number(selVar.stock||0)===0 : false)
          : hasAdvanced ? (Number(sel.stock||0)===0 || advSizeSoldOut)
          : Number(sel.stock||0)===0;
        return (
        <>
        <div style={{position:"fixed",inset:0,background:"var(--overlay)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}} onClick={()=>{setSel(null);setSelOpt("");setSelVar(null);setSelCustomAns("");setSelCustomImgs([]);}}>
          <div className="card pop-in" style={{width:"100%",maxWidth:700,margin:16,overflow:"hidden",maxHeight:"92vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr"}}>
              {/* Left: swipeable image */}
              <div>
                <ModalImageSwiper
                  images={selGalleryImages}
                  style={{ width:"100%" }}
                />
              </div>
              {/* Right: details */}
              <div style={{padding:isMobile?"20px 22px":32,display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <span className="pill">{sel.category||sel.cat}</span>
                    <button onClick={()=>{setSel(null);setSelOpt("");setSelVar(null);setSelCustomAns("");setSelCustomImgs([]);}} aria-label="Close" style={{background:"#B42318",border:"none",cursor:"pointer",padding:0,width:30,height:30,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}><Icon n="x" size={17} color="#fff"/></button>
                  </div>
                  <h2 style={{fontSize:isMobile?20:24,marginTop:10}}>{sel.name}</h2>
                  <div style={{display:"flex",alignItems:"baseline",gap:10,marginTop:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:32,fontWeight:700,color:"var(--pink-btn)"}}>₹{(Number(activePrice)||0).toLocaleString()}</span>
                    {sel.mrp&&Number(sel.mrp)>Number(sel.price)&&<span style={{fontSize:16,color:"var(--ink-soft)",textDecoration:"line-through"}}>₹{Number(sel.mrp).toLocaleString()}</span>}
                    {sel.mrp&&Number(sel.mrp)>Number(sel.price)&&<span style={{fontSize:12,background:"var(--green-bg)",color:"var(--green)",borderRadius:4,padding:"3px 10px",fontWeight:700}}>{Math.round((Number(sel.mrp)-Number(sel.price))/Number(sel.mrp)*100)}% off</span>}
                  </div>
                  {sel.free_shipping
                    ? <p style={{fontSize:12,color:"var(--green)",fontWeight:700,marginTop:4}}><Icon n="truck" size="1.1em" inline /> Free shipping</p>
                    : (Number(storeUser?.shipping_cost)||0)>0 && <p style={{fontSize:12,color:"var(--ink-soft)",marginTop:4}}>+ ₹{storeUser.shipping_cost} shipping</p>
                  }
                  {sel.bogo_eligible && bogoLabel(storeUser) && (
                    <p style={{fontSize:12,color:"var(--warn-fg)",background:"var(--warn-bg)",border:"1px solid var(--warn-bd)",borderRadius:4,padding:"3px 10px",fontWeight:800,marginTop:6,display:"inline-block"}}><Icon n="bag" size={12} inline /> {bogoLabel(storeUser)}</p>
                  )}
                  {storeUser?.dispatch_days&&<p style={{fontSize:12,color:"var(--ink-soft)",marginTop:2}}><Icon n="truck" size="1.1em" inline /> Ships in {storeUser.dispatch_days}</p>}
                  {sel.description&&(
                    <p style={{fontSize:13,color:"var(--ink)",lineHeight:1.65,marginTop:12,whiteSpace:"pre-line"}}>{sel.description}</p>
                  )}
                </div>

                {/* Variations selector */}
                {hasVariations&&(
                  <div>
                    <label style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",display:"block",marginBottom:8,color:"var(--ink-soft)"}}>SELECT VARIATION</label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                      {sel.variations.map((v,i)=>{
                        const vSoldOut = Number(v.stock||0)===0;
                        return (
                          <button key={i} onClick={()=>{ if(vSoldOut) return; setSelVar(cur => cur?.name===v.name ? null : v); setQty(q=>Math.min(q, Math.max(1, Number(v.stock||1)))); }}
                            style={{background:selVar?.name===v.name?"var(--pink-btn)":vSoldOut?"rgba(0,0,0,0.04)":"var(--surface)",color:selVar?.name===v.name?"var(--on-accent)":vSoldOut?"var(--ink-soft)":"var(--ink)",border:`1.5px solid ${selVar?.name===v.name?"var(--pink-btn)":"var(--border-mid)"}`,borderRadius:4,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:vSoldOut?"not-allowed":"pointer",transition:"all 0.15s",opacity:vSoldOut?0.5:1}}>
                            {v.name}<br/>
                            <span style={{fontSize:13,fontWeight:700}}>₹{(Number(v.price)||0).toLocaleString()}</span>
                            {vSoldOut&&<span style={{fontSize:10,color:"var(--red)",display:"block"}}>Sold out</span>}
                          </button>
                        );
                      })}
                    </div>
                    {hasVariations&&!selVar&&<p style={{fontSize:11,color:"var(--red)",marginTop:5}}>Please select a variation</p>}
                  </div>
                )}

                {/* Advanced variations selector — one pick from each group the seller filled */}
                {hasAdvanced&&advGroups.map(g=>(
                  <div key={g.key}>
                    <label style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",display:"block",marginBottom:8,color:"var(--ink-soft)"}}>{g.label}</label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                      {g.values.map((v,i)=>{
                        const vSoldOut = g.isSize && v.stock!=null && Number(v.stock)===0;
                        const chosen = selAdv[g.key]===v.name;
                        return (
                          <button key={i} onClick={()=>{ if(vSoldOut) return; setSelAdv(s=>({...s,[g.key]:v.name})); setQty(1); }}
                            style={{background:chosen?"var(--pink-btn)":vSoldOut?"rgba(0,0,0,0.04)":"var(--surface)",color:chosen?"var(--on-accent)":vSoldOut?"var(--ink-soft)":"var(--ink)",border:`1.5px solid ${chosen?"var(--pink-btn)":"var(--border-mid)"}`,borderRadius:4,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:vSoldOut?"not-allowed":"pointer",transition:"all 0.15s",opacity:vSoldOut?0.5:1}}>
                            {v.name}
                            {Number(v.price||0)>0&&<span style={{fontSize:11,fontWeight:700,marginLeft:5,opacity:0.85}}>+₹{Number(v.price).toLocaleString()}</span>}
                            {vSoldOut&&<span style={{fontSize:10,color:"var(--red)",display:"block"}}>Sold out</span>}
                          </button>
                        );
                      })}
                    </div>
                    {!selAdv[g.key]&&<p style={{fontSize:11,color:"var(--red)",marginTop:5}}>Please choose a {g.label.toLowerCase()}</p>}
                  </div>
                ))}

                <div>
                  <label style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",display:"block",marginBottom:7,color:"var(--ink-soft)"}}>QUANTITY</label>
                  <div style={{display:"flex",alignItems:"center",gap:14}}>
                    <button
                      onClick={()=>setQty(q=>Math.max(1,q-1))}
                      disabled={needsSelection&&!selectionMade}
                      style={{background:needsSelection&&!selectionMade?"var(--surface)":"var(--pink)",border:"none",width:34,height:34,borderRadius:4,fontSize:18,cursor:needsSelection&&!selectionMade?"not-allowed":"pointer",color:needsSelection&&!selectionMade?"var(--ink-soft)":"var(--pink-btn-h)",opacity:needsSelection&&!selectionMade?0.4:1}}>−</button>
                    <span style={{fontWeight:700,fontSize:17,opacity:needsSelection&&!selectionMade?0.4:1}}>{qty}</span>
                    <button
                      onClick={()=>setQty(q=>Math.min(q+1, activeStock))}
                      disabled={qty>=activeStock||(needsSelection&&!selectionMade)}
                      style={{background:qty>=activeStock||(needsSelection&&!selectionMade)?"var(--surface)":"var(--pink)",border:"none",width:34,height:34,borderRadius:4,fontSize:18,cursor:qty>=activeStock||(needsSelection&&!selectionMade)?"not-allowed":"pointer",color:qty>=activeStock||(needsSelection&&!selectionMade)?"var(--ink-soft)":"var(--pink-btn-h)",opacity:qty>=activeStock||(needsSelection&&!selectionMade)?0.4:1}}>+</button>
                  </div>
                  {needsSelection&&!selectionMade&&<p style={{fontSize:11,color:"var(--ink-soft)",marginTop:6}}>{hasAdvanced?"Choose all options to set quantity":"Select a variation to set quantity"}</p>}
                  {(!needsSelection||selectionMade)&&activeStock>0&&activeStock<=5&&<p style={{fontSize:11,color:"var(--red)",fontWeight:700,marginTop:6}}>Only {activeStock} left!</p>}
                </div>
                {/* Custom question from seller */}
                {sel.custom_question && (
                  <div style={{background:"var(--warn-bg)",borderRadius:4,padding:"14px 16px",border:"1.5px solid var(--warn-bd)"}}>
                    <label style={{display:"block",fontSize:13,fontWeight:700,color:"var(--warn-fg)",marginBottom:8,lineHeight:1.4}}>
                      <Icon n="chat" size={13} inline /> {sel.custom_question} <span style={{color:"var(--red)",marginLeft:2}}>*</span>
                    </label>
                    <textarea
                      value={selCustomAns}
                      onChange={e=>setSelCustomAns(e.target.value.slice(0,500))}
                      placeholder="Type your answer here…"
                      rows={3}
                      style={{width:"100%",padding:"10px 12px",border:`1.5px solid ${selCustomAns.trim()?"rgba(245,158,11,0.5)":"var(--border)"}`,borderRadius:4,fontSize:13,fontFamily:"inherit",resize:"vertical",boxSizing:"border-box",background:"var(--white)",color:"var(--ink)",lineHeight:1.5,outline:"none",transition:"border-color 0.2s"}}
                    />
                    <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                      <span style={{fontSize:11,color:"var(--warn-fg)",fontWeight:600}}>Required — the seller needs this to fulfil your order</span>
                      <span style={{fontSize:11,color:"var(--ink-soft)"}}>{selCustomAns.length}/500</span>
                    </div>
                  </div>
                )}

                {/* Required image upload from customer */}
                {sel.require_upload && (
                  <div style={{background:"var(--info-bg)",borderRadius:4,padding:"14px 16px",border:"1.5px solid var(--info-bd)"}}>
                    <label style={{display:"block",fontSize:13,fontWeight:700,color:"var(--info-fg)",marginBottom:8,lineHeight:1.4}}>
                      <Icon n="camera" size="1.1em" inline /> Upload your image{qty>1?"s":""} for this order <span style={{color:"var(--red)",marginLeft:2}}>*</span>
                      {qty>1 && <span style={{display:"block",fontSize:11,fontWeight:500,color:"var(--ink-soft)",marginTop:2}}>One per item — add up to {qty} ({selCustomImgs.length}/{qty} added)</span>}
                    </label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                      {selCustomImgs.map((u,ix)=>(
                        <div key={ix} style={{position:"relative"}}>
                          <img src={u} alt={`Upload ${ix+1}`} style={{width:72,height:72,objectFit:"cover",borderRadius:4,border:"1.5px solid var(--info-bd)"}}/>
                          <button onClick={()=>setSelCustomImgs(prev=>prev.filter((_,j)=>j!==ix))} title="Remove" style={{position:"absolute",top:-7,right:-7,width:22,height:22,borderRadius:"50%",background:"var(--white)",border:"1.5px solid var(--border-mid)",cursor:"pointer",fontSize:13,lineHeight:1,color:"var(--ink-soft)"}}>×</button>
                        </div>
                      ))}
                      {selCustomImgs.length < Math.max(1, qty) && (
                        <label style={{width:72,height:72,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",border:"2px dashed rgba(59,130,246,0.35)",borderRadius:4,cursor:selImgUploading?"wait":"pointer",background:"var(--white)",gap:2}}>
                          <input type="file" accept="image/*" disabled={selImgUploading} style={{display:"none"}}
                            onChange={async e=>{
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (!file) return;
                              setSelImgUploading(true);
                              try {
                                const url = await uploadImageToSupabase(file, `customer-uploads/${storeUser?.id || "misc"}`);
                                setSelCustomImgs(prev=>[...prev, url]);
                              } catch(err) {
                                alert("Image upload failed: " + (err?.message || err) + "\nPlease try again.");
                              }
                              setSelImgUploading(false);
                            }}/>
                          <span style={{fontSize:22,fontWeight:700,color:"var(--info-fg)"}}>{selImgUploading ? "…" : "+"}</span>
                          <span style={{fontSize:9,color:"var(--ink-soft)"}}>{selImgUploading ? "" : "Add"}</span>
                        </label>
                      )}
                    </div>
                    <p style={{fontSize:11,color:"var(--ink-soft)",marginTop:8}}>The seller needs {qty>1?"these images":"this image"} to fulfil your order</p>
                  </div>
                )}

                <div ref={atcButtonsRef} style={{display:"flex",flexDirection:"column",gap:9}}>
                  {(()=>{
                    const ck = selVar ? `${sel.id}__${selVar.name}` : hasAdvanced && advComboLabel ? `${sel.id}__${advComboLabel}` : sel.id;
                    const inCart = cart.find(x=>(x.cartKey||x.id)===ck);
                    const cartQty = inCart?.qty || 0;
                    const canMore = cartQty < activeStock;
                    // Fully out-of-stock (no variations, or all variations depleted)
                    const fullyOutOfStock = hasVariations ? (selVar ? Number(selVar.stock||0)===0 : false) : Number(sel.stock||0)===0;
                    const globalSoldOut = !hasVariations && Number(sel.stock||0)===0;
                    if(globalSoldOut) return (
                      storeUser?.restock_enabled !== false
                        ? <div><SoldOutLabel block height={46} fontSize={15} onRestock={()=>{ setRestockProduct(sel); setSel(null); setSelOpt(""); setSelVar(null); setSelCustomAns("");setSelCustomImgs([]); }} hint="Tap to request a restock" /></div>
                        : <SoldOutLabel block height={46} fontSize={15} />
                    );
                    return (
                      <>
                        {!paymentsPaused && (inCart ? (
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--pink-btn)",borderRadius:4,padding:"6px 10px",height:50}}>
                            <button onClick={()=>{ cartQty<=1?remove(ck):updQ(ck,cartQty-1); }}
                              style={{background:"rgba(127,127,127,0.25)",border:"none",width:34,height:34,borderRadius:4,fontSize:20,cursor:"pointer",color:"var(--on-accent)",fontWeight:700}}>−</button>
                            <span style={{fontWeight:700,fontSize:14,color:"var(--on-accent)"}}><Icon n="bag" size={14} inline /> {cartQty} in cart</span>
                            <button onClick={()=>updQ(ck,cartQty+1)} disabled={!canMore}
                              style={{background:canMore?"rgba(127,127,127,0.25)":"transparent",border:"none",width:34,height:34,borderRadius:4,fontSize:20,cursor:canMore?"pointer":"not-allowed",color:"var(--on-accent)",fontWeight:700,opacity:canMore?1:0.3}}>+</button>
                          </div>
                        ) : (
                          <button className="btn-pink" style={{padding:13,borderRadius:4,opacity:((sel.custom_question&&!selCustomAns.trim())||(sel.require_upload&&!selCustomImgs.length))?0.45:1,cursor:((sel.custom_question&&!selCustomAns.trim())||(sel.require_upload&&!selCustomImgs.length))?"not-allowed":"pointer"}}
                            onClick={()=>{
                              if(needsSelection&&!selectionMade){alert(hasAdvanced?"Please choose all options first":"Please select a variation first");return;}
                              if(hasVariations&&selVar&&Number(selVar.stock||0)===0){alert("This variation is sold out");return;}
                              if(hasAdvanced&&advSizeSoldOut){alert("This size is sold out");return;}
                              if(sel.custom_question&&!selCustomAns.trim()){alert("Please answer the store's question first");return;}
                              if(sel.require_upload&&!selCustomImgs.length){alert("Please upload the required image first");return;}
                              addCart({...sel, price:activePrice,
                                ...(hasAdvanced ? {selectedCombo:advComboLabel, selectedAdvanced:{...selAdv}} : {selectedVariation:selVar, selectedOption:selVar?.name||selOpt})});
                            }}>
                            <Icon n="bag" size="1.1em" inline /> Add to Cart
                          </button>
                        ))}
                        {/* MOP (Mode-of-Payment) picker — only when the feature is on for this
                            viewer. No rupee amounts shown here (this modal has no existing price
                            breakdown to extend, and duplicating the shipping/fee math just for a
                            preview would risk it drifting from the real, server-computed total —
                            exactly the class of bug the no-drift check in Cart.jsx guards against).
                            The actual, correct amount for the chosen method appears at the
                            Razorpay step, same as today's flow already does. */}
                        {!paymentsPaused && mop.enabled && (
                          <div style={{display:"flex",gap:6,marginBottom:2}}>
                            {Object.entries(mop.methods).map(([key, m]) => (
                              <button key={key} type="button" onClick={()=>setPayMethod(key)}
                                style={{flex:1,padding:"6px 8px",borderRadius:4,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                                  border: payMethod===key ? "2px solid var(--pink-btn)" : "1px solid var(--border)",
                                  background: payMethod===key ? "rgba(47,99,221,0.08)" : "var(--surface)",
                                  color: "var(--ink)"}}>
                                {m.label}{payMethod===key ? " ✓" : ""}
                              </button>
                            ))}
                          </div>
                        )}
                        {paymentsPaused ? (
                          <button disabled style={{padding:13,borderRadius:4,border:"none",background:"rgba(0,0,0,0.08)",color:"var(--ink-soft)",cursor:"not-allowed",fontWeight:600,fontSize:14,width:"100%"}}>
                            <Icon n="locked" size="1.1em" inline /> Payments Paused
                          </button>
                        ) : (
                          <button className="btn-green" style={{padding:13,borderRadius:4,opacity:((sel.custom_question&&!selCustomAns.trim())||(sel.require_upload&&!selCustomImgs.length))?0.45:1,cursor:((sel.custom_question&&!selCustomAns.trim())||(sel.require_upload&&!selCustomImgs.length))?"not-allowed":"pointer"}}
                            onClick={()=>{
                              if(sel.custom_question&&!selCustomAns.trim()){alert("Please answer the store's question first");return;}
                              if(sel.require_upload&&!selCustomImgs.length){alert("Please upload the required image first");return;}
                              if(needsSelection&&!selectionMade){alert(hasAdvanced?"Please choose all options first":"Please select a variation first");return;}
                              if(hasVariations&&selVar&&Number(selVar.stock||0)===0){alert("This variation is sold out");return;}
                              if(hasAdvanced&&advSizeSoldOut){alert("This size is sold out");return;}
                              payWithCard({...sel, price:activePrice,
                                ...(hasAdvanced ? { selectedCombo:advComboLabel, selectedAdvanced:{...selAdv} } : {}),
                                ...(sel.custom_question ? { custom_question: sel.custom_question, custom_answer: selCustomAns.trim() } : {}),
                                ...(sel.require_upload && selCustomImgs.length ? { custom_images: selCustomImgs, custom_image: selCustomImgs[0] } : {}),
                              }, inCart?cartQty:qty, hasAdvanced ? "" : (selVar?.name||selOpt));
                            }}>
                            <Icon n="instant" size="1.1em" inline /> Pay Now
                            {sel.free_shipping && <span style={{fontSize:11,opacity:0.9,fontWeight:700}}> · Free shipping</span>}
                          </button>
                        )}
                      </>
                    );
                  })()}
                  <p style={{fontSize:11,color:"var(--ink-soft)",textAlign:"center",marginTop:2}}>UPI · Cards · Wallets · Secured by Razorpay</p>
                </div>
              </div>
            </div>
            {/* ── Reviews, Q&A & more from this store — parity with the full product page,
                 rendered below the buy area inside the scrollable modal so shoppers get the
                 same trust signals without leaving the store. ── */}
            <div style={{padding:isMobile?"0 22px 26px":"0 32px 34px"}}>
              <ProductReviewsQNA productId={sel.id} storeId={storeUser?.id} customer={customer} productOwnerId={storeUser?.id} qaEnabled={storeUser?.qa_enabled !== false}/>
              <ProductRecommendations currentProductId={sel.id} currentStoreId={storeUser?.id} storeOnly/>
            </div>
          </div>
        </div>

        {/* ── Sticky ATC bar — slides up when buy buttons scroll off screen ── */}
        {showAtcBar && !soldOut && (
          <div style={{
            position:"fixed",bottom:0,left:0,right:0,zIndex:350,
            background:"var(--white)",borderTop:"1.5px solid var(--border)",
            padding:isMobile?"10px 14px":"11px 20px",
            display:"flex",alignItems:"center",gap:isMobile?10:14,
            boxShadow:"0 -6px 28px rgba(0,0,0,0.11)",
            animation:"atcSlideUp 0.22s cubic-bezier(.22,.68,0,1.2) both",
          }}>
            <img
              src={thumbUrl(selImages[0]||sel.img, 80)}
              alt={sel.name}
              style={{width:isMobile?40:46,height:isMobile?40:46,objectFit:"cover",borderRadius:4,flexShrink:0,border:"1.5px solid var(--border)"}}
            />
            <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
              <p style={{fontWeight:700,fontSize:isMobile?13:14,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",margin:0}}>{sel.name}</p>
              {selVar
                ? <p style={{fontSize:isMobile?11:12,color:"var(--ink-soft)",margin:0,marginTop:1}}>{selVar.name} · ₹{Number(selVar.price).toLocaleString()}</p>
                : hasAdvanced && advComboLabel
                ? <p style={{fontSize:isMobile?11:12,color:"var(--ink-soft)",margin:0,marginTop:1}}>{advComboLabel} · ₹{Number(activePrice).toLocaleString()}</p>
                : <p style={{fontSize:isMobile?11:12,color:"var(--ink-soft)",margin:0,marginTop:1}}>₹{Number(sel.price).toLocaleString()}{needsSelection?(hasAdvanced?" · Select options":" · Select variation"):""}</p>
              }
            </div>
            <span style={{fontWeight:800,fontSize:isMobile?15:17,color:"var(--pink-btn)",flexShrink:0}}>
              ₹{(activePrice*qty).toLocaleString()}
            </span>
            <button
              className="btn-pink"
              style={{borderRadius:4,padding:isMobile?"9px 14px":"10px 20px",fontSize:isMobile?12:13,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}
              onClick={()=>{
                if(needsSelection&&!selectionMade){alert(hasAdvanced?"Please choose all options first":"Please select a variation first");return;}
                if(hasVariations&&selVar&&Number(selVar.stock||0)===0){alert("This variation is sold out");return;}
                if(hasAdvanced&&advSizeSoldOut){alert("This size is sold out");return;}
                addCart({...sel, price:activePrice,
                  ...(hasAdvanced ? {selectedCombo:advComboLabel, selectedAdvanced:{...selAdv}} : {selectedVariation:selVar, selectedOption:selVar?.name||selOpt})});
              }}>
              <Icon n="bag" size="1.1em" inline /> Add to Cart
            </button>
          </div>
        )}
        </>
        );
      })()}

      {/* ── Restock Request Modal ── */}
      {restockProduct && (
        <RestockRequestModal
          product={restockProduct}
          storeId={storeUser?.id}
          customer={customer}
          onClose={() => setRestockProduct(null)}
          onSuccess={() => {}}
        />
      )}

    </div>
    </ThemeWrapper>
  );
}
